import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  User,
  Tenant,
  PlatformMetrics,
  Plan,
  SuperAdminAccount,
  TenantAdmin,
  PlatformAuditLog,
  PlatformUsage,
  PlatformSettings,
} from '../types';
import {
  getSuperAdminTenants,
  getSuperAdminMetrics,
  updateTenantPlan,
  updateTenantTrial,
  suspendTenant,
  activateTenant,
  createSuperAdmin,
  changeSuperAdminPassword,
  createTenant,
  updateTenantAdmin,
  getSuperAdmins,
  deactivateSuperAdmin,
  activateSuperAdmin,
  getTenantAdmins,
  createTenantAdmin,
  updateTenantAdminById,
  getSuperAdminAuditLogs,
  getSuperAdminUsage,
  SuperAdminAuditLogFilters,
  getPlans,
  createPlan,
  updatePlan,
  deletePlan,
  getSuperAdminUsers,
  getSignupAlertRecipients,
  resendPlatformUserVerification,
  resetPlatformUserPassword,
  createSignupAlertRecipient,
  deleteSignupAlertRecipient,
  SuperAdminUser,
  SignupAlertRecipient,
  suspendPlatformUser,
  unsuspendPlatformUser,
  updateSignupAlertRecipient,
  getPlatformSettings,
  updatePlatformSetting,
} from '../services/api';
import AlertToast from '../components/AlertToast';
import { useIsMobile } from '../hooks/useIsMobile';
import SuperAdminSupport from './SuperAdminSupport';
import SuperAdminTenantHealth from './SuperAdminTenantHealth';

interface SuperAdminProps {
  user: User;
  onLogout: () => void;
  onImpersonate: (tenantId: string) => void;
}

type SuperAdminTab = 'tenants' | 'users' | 'plans' | 'metrics' | 'health' | 'support' | 'audit' | 'super-admins';
type CommandCenterTab = Extract<SuperAdminTab, 'users' | 'support' | 'health'>;
type Notice = { type: 'success' | 'error'; message: string } | null;
type PlanFormState = {
  id?: string;
  name: string;
  slug: string;
  features: string[];
  max_devices: string;
  max_data_streams: string;
  max_users: string;
  max_dashboards: string;
  price_monthly: string;
  currency: string;
  is_public: boolean;
};
type PlatformSettingsForm = {
  default_trial_days: string;
  trial_plan_slug: string;
  support_email: string;
};
type AuditFiltersState = {
  tenant_id: string;
  event_type: string;
  severity: string;
  action: string;
  search: string;
  start: string;
  end: string;
};

const ALL_FEATURES = [
  { id: 'overview', label: 'Overview & Live Sensors' },
  { id: 'map', label: 'Floor Plan' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'devices', label: 'Devices' },
  { id: 'sites', label: 'Sites & Areas' },
  { id: 'mqtt', label: 'MQTT Bridges' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'export', label: 'Data Export' },
  { id: 'reports', label: 'Reports' },
  { id: 'team', label: 'Team' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'settings', label: 'Settings' },
];

const NAV_ITEMS: Array<{ id: SuperAdminTab; label: string; icon: SuperAdminIconName }> = [
  { id: 'tenants', label: 'Tenants', icon: 'building' },
  { id: 'users', label: 'Users', icon: 'users' },
  { id: 'plans', label: 'Plans', icon: 'layers' },
  { id: 'metrics', label: 'Platform Metrics', icon: 'chart' },
  { id: 'health', label: 'Tenant Health', icon: 'health' },
  { id: 'support', label: 'Support', icon: 'support' },
  { id: 'audit', label: 'Audit Logs', icon: 'audit' },
  { id: 'super-admins', label: 'Super Admins', icon: 'shield' },
];

const COMMAND_CENTER_TABS: Array<{ id: CommandCenterTab; label: string; detail: string }> = [
  { id: 'users', label: 'Users', detail: 'Scan every user and workspace without expanding the page height.' },
  { id: 'support', label: 'Support', detail: 'Triage tickets, replies, assignments, and structured resolutions.' },
  { id: 'health', label: 'Tenant Health', detail: 'Review customer risk signals with a matrix-first operating view.' },
];

function isSuperAdminTab(value: string | null): value is SuperAdminTab {
  return !!value && NAV_ITEMS.some((item) => item.id === value);
}

function isCommandCenterTab(value: SuperAdminTab): value is CommandCenterTab {
  return COMMAND_CENTER_TABS.some((item) => item.id === value);
}

const AUDIT_PAGE_SIZE = 50;
const EMPTY_AUDIT_FILTERS: AuditFiltersState = {
  tenant_id: '',
  event_type: '',
  severity: '',
  action: '',
  search: '',
  start: '',
  end: '',
};

export default function SuperAdmin({ user, onLogout, onImpersonate }: SuperAdminProps) {
  const isMobile = useIsMobile(860);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabStorageKey = `superAdmin.activeTab.${user.id}`;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [superAdmins, setSuperAdmins] = useState<SuperAdminAccount[]>([]);
  const [usage, setUsage] = useState<PlatformUsage | null>(null);
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<SuperAdminTab>(() => {
    const urlTab = searchParams.get('tab');
    if (isSuperAdminTab(urlTab)) return urlTab;
    const savedTab = localStorage.getItem(activeTabStorageKey);
    if (isSuperAdminTab(savedTab)) return savedTab;
    return 'tenants';
  });
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [editingAdmin, setEditingAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', password: '' });
  const [search, setSearch] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const [createTenantForm, setCreateTenantForm] = useState({
    tenant_name: '',
    admin_email: '',
    admin_password: '',
    plan: 'free',
  });
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [trialActionId, setTrialActionId] = useState('');
  const [trialEndAt, setTrialEndAt] = useState('');
  const [planForm, setPlanForm] = useState<PlanFormState | null>(null);
  const [platformSettingsForm, setPlatformSettingsForm] = useState<PlatformSettingsForm>({
    default_trial_days: '30',
    trial_plan_slug: 'enterprise',
    support_email: 'admin@seedronics.com',
  });
  const [savingPlatformSettings, setSavingPlatformSettings] = useState(false);
  const [tenantAdmins, setTenantAdmins] = useState<TenantAdmin[]>([]);
  const [addAdminForm, setAddAdminForm] = useState({ email: '', password: '' });
  const [addingTenantAdmin, setAddingTenantAdmin] = useState(false);
  const [editingTenantAdmin, setEditingTenantAdmin] = useState<{ id: string; email: string; password: string } | null>(null);
  const [auditFilters, setAuditFilters] = useState<AuditFiltersState>(EMPTY_AUDIT_FILTERS);
  const [auditLogs, setAuditLogs] = useState<PlatformAuditLog[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [platformUsers, setPlatformUsers] = useState<SuperAdminUser[]>([]);
  const [platformUserSearch, setPlatformUserSearch] = useState('');
  const [platformUsersLoading, setPlatformUsersLoading] = useState(false);
  const [platformUserAction, setPlatformUserAction] = useState('');
  const [selectedPlatformUserId, setSelectedPlatformUserId] = useState('');
  const [signupAlertRecipients, setSignupAlertRecipients] = useState<SignupAlertRecipient[]>([]);
  const [signupAlertRecipientsLoading, setSignupAlertRecipientsLoading] = useState(false);
  const [signupAlertAction, setSignupAlertAction] = useState('');
  const [signupAlertForm, setSignupAlertForm] = useState({ email: '', label: '', enabled: true });
  const [healthRefreshSignal, setHealthRefreshSignal] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    localStorage.setItem(activeTabStorageKey, activeTab);
    if (searchParams.get('tab') !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, activeTabStorageKey, searchParams, setSearchParams]);

  useEffect(() => {
    setEditingAdmin(false);
    setAdminForm({ email: '', password: '' });
    setTenantAdmins([]);
    setAddAdminForm({ email: '', password: '' });
    setEditingTenantAdmin(null);
    setTrialEndAt(selectedTenant ? formatTrialDateInput(selectedTenant.trial_ends_at, getDefaultTrialDays(platformSettingsForm.default_trial_days)) : '');
    if (!selectedTenant) return;
    getTenantAdmins(selectedTenant.id)
      .then(setTenantAdmins)
      .catch((err: any) => {
        setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to load tenant admins.' });
      });
  }, [platformSettingsForm.default_trial_days, selectedTenant?.id]);

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditLogs(0);
    }
    if (activeTab === 'users') {
      loadPlatformUsers();
      loadSignupAlertRecipients();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    if (platformUsers.length === 0) {
      setSelectedPlatformUserId('');
      return;
    }
    if (!selectedPlatformUserId || !platformUsers.some((account) => account.id === selectedPlatformUserId)) {
      setSelectedPlatformUserId(platformUsers[0].id);
    }
  }, [activeTab, platformUsers, selectedPlatformUserId]);

  async function loadData() {
    setLoading(true);
    try {
      const [tenantList, platformMetrics, planList, adminList, usageData, settings] = await Promise.all([
        getSuperAdminTenants(),
        getSuperAdminMetrics(),
        getPlans(),
        getSuperAdmins(),
        getSuperAdminUsage(30),
        getPlatformSettings(),
      ]);
      setTenants(tenantList);
      setMetrics(platformMetrics);
      setPlans(planList);
      setSuperAdmins(adminList);
      setUsage(usageData);
      setPlatformSettings(settings);
      setPlatformSettingsForm({
        default_trial_days: settings.default_trial_days || '30',
        trial_plan_slug: settings.trial_plan_slug || 'enterprise',
        support_email: settings.support_email || 'admin@seedronics.com',
      });
      setNotice(null);
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to load super admin data.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    await loadData();
    if (activeTab === 'health') {
      setHealthRefreshSignal(Date.now());
    }
  }

  async function handlePlanChange(tenantId: string, plan: string) {
    try {
      const result = await updateTenantPlan(tenantId, plan);
      setTenants((prev) => prev.map((tenant) => (tenant.id === tenantId ? mergeTenantPlanUpdate(tenant, result, plan) : tenant)));
      if (selectedTenant?.id === tenantId) {
        setSelectedTenant((prev) => (prev ? mergeTenantPlanUpdate(prev, result, plan) : prev));
      }
      setNotice({ type: 'success', message: 'Tenant plan updated.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to update plan.' });
    }
  }

  function applyTenantTrialUpdate(tenantId: string, update: Partial<Tenant>) {
    setTenants((prev) => prev.map((tenant) => (tenant.id === tenantId ? mergeTenantTrialUpdate(tenant, update, platformSettingsForm.trial_plan_slug) : tenant)));
    if (selectedTenant?.id === tenantId) {
      setSelectedTenant((prev) => (prev ? mergeTenantTrialUpdate(prev, update, platformSettingsForm.trial_plan_slug) : prev));
    }
  }

  async function handleTrialToggle(tenant: Tenant) {
    const clearing = tenant.is_trial === true;
    if (clearing && !window.confirm(`End the trial for ${tenant.name}? The tenant's base plan will stay as ${formatPlanLabel(getTenantBasePlanSlug(tenant))}.`)) return;

    const defaultTrialDays = getDefaultTrialDays(platformSettingsForm.default_trial_days);
    const trialEndsAt = clearing ? null : getTrialEndIso(defaultTrialDays);
    setTrialActionId(tenant.id);
    try {
      const result = await updateTenantTrial(tenant.id, trialEndsAt);
      applyTenantTrialUpdate(tenant.id, result);
      if (selectedTenant?.id === tenant.id) {
        setTrialEndAt(formatTrialDateInput(result.trial_ends_at, defaultTrialDays));
      }
      setNotice({
        type: 'success',
        message: clearing
          ? 'Tenant trial deactivated. Stored plan was not changed.'
          : `Tenant trial activated for ${result.days_remaining ?? defaultTrialDays} day${(result.days_remaining ?? defaultTrialDays) === 1 ? '' : 's'}.`,
      });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to update tenant trial.' });
    } finally {
      setTrialActionId('');
    }
  }

  async function handleSaveTenantTrial(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTenant || !trialEndAt) return;
    const trialEndsAt = trialDateInputToIso(trialEndAt);
    if (!trialEndsAt) {
      setNotice({ type: 'error', message: 'Choose a valid trial end date and time.' });
      return;
    }

    setTrialActionId(selectedTenant.id);
    try {
      const result = await updateTenantTrial(selectedTenant.id, trialEndsAt);
      applyTenantTrialUpdate(selectedTenant.id, result);
      setTrialEndAt(formatTrialDateInput(result.trial_ends_at, getDefaultTrialDays(platformSettingsForm.default_trial_days)));
      setNotice({ type: 'success', message: `Tenant trial now ends ${formatDateTime(result.trial_ends_at || trialEndsAt)}.` });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to save tenant trial.' });
    } finally {
      setTrialActionId('');
    }
  }

  async function handleClearTenantTrial() {
    if (!selectedTenant) return;
    if (!window.confirm(`Clear the trial for ${selectedTenant.name}? The tenant's base plan will not change.`)) return;
    setTrialActionId(selectedTenant.id);
    try {
      const result = await updateTenantTrial(selectedTenant.id, null);
      applyTenantTrialUpdate(selectedTenant.id, result);
      setTrialEndAt(formatTrialDateInput(null, getDefaultTrialDays(platformSettingsForm.default_trial_days)));
      setNotice({ type: 'success', message: 'Tenant trial cleared. Stored plan was not changed.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to clear tenant trial.' });
    } finally {
      setTrialActionId('');
    }
  }

  async function handleSuspend(tenantId: string) {
    if (!window.confirm('Suspend this tenant? Members will be unable to perform mutations.')) return;
    const suspendedAt = new Date().toISOString();
    try {
      await suspendTenant(tenantId);
      setTenants((prev) => prev.map((tenant) => (
        tenant.id === tenantId ? { ...tenant, is_active: false, suspended_at: suspendedAt } : tenant
      )));
      if (selectedTenant?.id === tenantId) {
        setSelectedTenant((prev) => (prev ? { ...prev, is_active: false, suspended_at: suspendedAt } : prev));
      }
      setNotice({ type: 'success', message: 'Tenant suspended.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to suspend tenant.' });
    }
  }

  async function handleActivate(tenantId: string) {
    try {
      await activateTenant(tenantId);
      setTenants((prev) => prev.map((tenant) => (
        tenant.id === tenantId ? { ...tenant, is_active: true, suspended_at: null } : tenant
      )));
      if (selectedTenant?.id === tenantId) {
        setSelectedTenant((prev) => (prev ? { ...prev, is_active: true, suspended_at: null } : prev));
      }
      setNotice({ type: 'success', message: 'Tenant activated.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to activate tenant.' });
    }
  }

  async function handleCreateSuperAdmin(event: React.FormEvent) {
    event.preventDefault();
    if (!newAdminEmail || !newAdminPassword) return;
    setCreatingAdmin(true);
    try {
      await createSuperAdmin({ email: newAdminEmail, password: newAdminPassword });
      setNewAdminEmail('');
      setNewAdminPassword('');
      setSuperAdmins(await getSuperAdmins());
      setNotice({ type: 'success', message: 'Super admin created.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to create super admin.' });
    } finally {
      setCreatingAdmin(false);
    }
  }

  async function handleChangePassword(event: React.FormEvent) {
    event.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      setNotice({ type: 'error', message: 'New passwords do not match.' });
      return;
    }
    setChangingPassword(true);
    try {
      await changeSuperAdminPassword({
        current_password: passwordForm.current,
        new_password: passwordForm.new,
      });
      setPasswordForm({ current: '', new: '', confirm: '' });
      setNotice({ type: 'success', message: 'Password updated.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to change password.' });
    } finally {
      setChangingPassword(false);
    }
  }

  async function handleCreateTenant(event: React.FormEvent) {
    event.preventDefault();
    if (!createTenantForm.tenant_name || !createTenantForm.admin_email || !createTenantForm.admin_password) return;
    setCreatingTenant(true);
    try {
      const result = await createTenant({
        tenant_name: createTenantForm.tenant_name,
        admin_email: createTenantForm.admin_email,
        admin_password: createTenantForm.admin_password,
        plan: createTenantForm.plan,
      });
      setCreateTenantForm({ tenant_name: '', admin_email: '', admin_password: '', plan: 'free' });
      setTenants((prev) => [
        {
          ...result.tenant,
          device_count: 0,
          user_count: 1,
          alert_count: 0,
          timezone: 'Africa/Lagos',
          logo_url: null,
          base_plan: result.tenant.base_plan || result.tenant.plan || 'free',
          effective_plan: result.tenant.effective_plan || result.tenant.plan || 'free',
          trial_ends_at: result.tenant.trial_ends_at || null,
          is_trial: result.tenant.is_trial || false,
          days_remaining: result.tenant.days_remaining || 0,
        },
        ...prev,
      ]);
      setNotice({ type: 'success', message: `Tenant created. Admin: ${result.admin.email}` });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to create tenant.' });
    } finally {
      setCreatingTenant(false);
    }
  }

  async function handleAddTenantAdmin(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTenant || !addAdminForm.email || !addAdminForm.password) return;
    setAddingTenantAdmin(true);
    try {
      await createTenantAdmin(selectedTenant.id, {
        email: addAdminForm.email,
        password: addAdminForm.password,
      });
      setAddAdminForm({ email: '', password: '' });
      setTenantAdmins(await getTenantAdmins(selectedTenant.id));
      setNotice({ type: 'success', message: 'Tenant admin added.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to add tenant admin.' });
    } finally {
      setAddingTenantAdmin(false);
    }
  }

  async function handleSaveTenantAdmin(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTenant || !editingTenantAdmin) return;
    const payload: { email?: string; password?: string } = {};
    if (editingTenantAdmin.email.trim()) payload.email = editingTenantAdmin.email.trim();
    if (editingTenantAdmin.password) payload.password = editingTenantAdmin.password;
    if (!payload.email && !payload.password) {
      setNotice({ type: 'error', message: 'Enter a new email or password.' });
      return;
    }
    try {
      await updateTenantAdminById(selectedTenant.id, editingTenantAdmin.id, payload);
      setEditingTenantAdmin(null);
      setTenantAdmins(await getTenantAdmins(selectedTenant.id));
      setNotice({ type: 'success', message: 'Tenant admin updated.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to update tenant admin.' });
    }
  }

  async function handleDeactivateSuperAdmin(admin: SuperAdminAccount) {
    if (!window.confirm(`Deactivate ${admin.email}? They will no longer be able to sign in.`)) return;
    try {
      await deactivateSuperAdmin(admin.id);
      setSuperAdmins((prev) => prev.map((item) => (
        item.id === admin.id ? { ...item, is_active: false, deactivated_at: new Date().toISOString() } : item
      )));
      setNotice({ type: 'success', message: 'Super admin deactivated.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to deactivate super admin.' });
    }
  }

  async function handleActivateSuperAdmin(admin: SuperAdminAccount) {
    try {
      await activateSuperAdmin(admin.id);
      setSuperAdmins((prev) => prev.map((item) => (
        item.id === admin.id ? { ...item, is_active: true, deactivated_at: null } : item
      )));
      setNotice({ type: 'success', message: 'Super admin activated.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to activate super admin.' });
    }
  }

  function handleImpersonateClick(tenant: Tenant) {
    if (window.confirm(`Log in as ${tenant.name}? Your super admin session will be kept so you can switch back.`)) {
      onImpersonate(tenant.id);
    }
  }

  async function loadAuditLogs(page: number, filters: AuditFiltersState = auditFilters) {
    setAuditLoading(true);
    try {
      const params: SuperAdminAuditLogFilters = {
        limit: AUDIT_PAGE_SIZE,
        offset: page * AUDIT_PAGE_SIZE,
      };
      if (filters.tenant_id) params.tenant_id = filters.tenant_id;
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.severity) params.severity = filters.severity;
      if (filters.action.trim()) params.action = filters.action.trim();
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const response = await getSuperAdminAuditLogs(params);
      setAuditLogs(response.logs || []);
      setAuditTotal(response.total || 0);
      setAuditPage(page);
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to load platform audit logs.' });
    } finally {
      setAuditLoading(false);
    }
  }

  async function loadPlatformUsers(searchValue = platformUserSearch) {
    setPlatformUsersLoading(true);
    try {
      const users = await getSuperAdminUsers({
        search: searchValue.trim() || undefined,
        limit: 100,
        offset: 0,
      });
      setPlatformUsers(Array.isArray(users) ? users : []);
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to load platform users.' });
    } finally {
      setPlatformUsersLoading(false);
    }
  }

  async function loadSignupAlertRecipients() {
    setSignupAlertRecipientsLoading(true);
    try {
      const recipients = await getSignupAlertRecipients();
      setSignupAlertRecipients(Array.isArray(recipients) ? recipients : []);
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to load signup alert recipients.' });
    } finally {
      setSignupAlertRecipientsLoading(false);
    }
  }

  async function handlePlatformUserSearch(event: React.FormEvent) {
    event.preventDefault();
    await loadPlatformUsers(platformUserSearch);
  }

  async function handleSuspendPlatformUser(account: SuperAdminUser) {
    if (!window.confirm(`Suspend ${account.email}? Active sessions will be revoked.`)) return;
    setPlatformUserAction(account.id);
    try {
      await suspendPlatformUser(account.id);
      setPlatformUsers((prev) => prev.map((user) => (
        user.id === account.id ? { ...user, suspended_by_platform: true } : user
      )));
      setNotice({ type: 'success', message: `${account.email} suspended.` });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to suspend user.' });
    } finally {
      setPlatformUserAction('');
    }
  }

  async function handleUnsuspendPlatformUser(account: SuperAdminUser) {
    setPlatformUserAction(account.id);
    try {
      await unsuspendPlatformUser(account.id);
      setPlatformUsers((prev) => prev.map((user) => (
        user.id === account.id ? { ...user, suspended_by_platform: false } : user
      )));
      setNotice({ type: 'success', message: `${account.email} unsuspended.` });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to unsuspend user.' });
    } finally {
      setPlatformUserAction('');
    }
  }

  async function handleResetPlatformUserPassword(account: SuperAdminUser) {
    setPlatformUserAction(account.id);
    try {
      const result = await resetPlatformUserPassword(account.id);
      setNotice({
        type: 'success',
        message: result.reset_url
          ? `Password reset created for ${account.email}. Dev link: ${result.reset_url}`
          : result.message || `Password reset email queued for ${account.email}.`,
      });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to reset user password.' });
    } finally {
      setPlatformUserAction('');
    }
  }

  async function handleResendPlatformUserVerification(account: SuperAdminUser) {
    setPlatformUserAction(account.id);
    try {
      const result = await resendPlatformUserVerification(account.id);
      setNotice({
        type: 'success',
        message: result.verification_url
          ? `Verification link created for ${account.email}. Dev link: ${result.verification_url}`
          : result.message || `Verification email queued for ${account.email}.`,
      });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to resend verification.' });
    } finally {
      setPlatformUserAction('');
    }
  }

  async function handleCreateSignupAlertRecipient(event: React.FormEvent) {
    event.preventDefault();
    const email = signupAlertForm.email.trim();
    if (!email) return;
    setSignupAlertAction('create');
    try {
      const recipient = await createSignupAlertRecipient({
        email,
        label: signupAlertForm.label.trim() || null,
        enabled: signupAlertForm.enabled,
      });
      setSignupAlertRecipients((prev) => [recipient, ...prev.filter((item) => item.id !== recipient.id)]);
      setSignupAlertForm({ email: '', label: '', enabled: true });
      setNotice({ type: 'success', message: `${recipient.email} will receive signup alerts.` });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to add signup alert recipient.' });
    } finally {
      setSignupAlertAction('');
    }
  }

  async function handleToggleSignupAlertRecipient(recipient: SignupAlertRecipient) {
    setSignupAlertAction(recipient.id);
    try {
      const updated = await updateSignupAlertRecipient(recipient.id, { enabled: !recipient.enabled });
      setSignupAlertRecipients((prev) => prev.map((item) => (item.id === recipient.id ? updated : item)));
      setNotice({ type: 'success', message: `${updated.email} ${updated.enabled ? 'enabled' : 'paused'} for signup alerts.` });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to update signup alert recipient.' });
    } finally {
      setSignupAlertAction('');
    }
  }

  async function handleDeleteSignupAlertRecipient(recipient: SignupAlertRecipient) {
    if (!window.confirm(`Remove ${recipient.email} from signup alerts?`)) return;
    setSignupAlertAction(recipient.id);
    try {
      await deleteSignupAlertRecipient(recipient.id);
      setSignupAlertRecipients((prev) => prev.filter((item) => item.id !== recipient.id));
      setNotice({ type: 'success', message: `${recipient.email} removed from signup alerts.` });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to delete signup alert recipient.' });
    } finally {
      setSignupAlertAction('');
    }
  }

  function parseLimit(value: string): number | null {
    if (value.trim() === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue >= 0 ? Math.floor(numberValue) : null;
  }

  function parsePrice(value: string): number | null {
    if (value.trim() === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue >= 0 ? Number(numberValue.toFixed(2)) : null;
  }

  async function handleUpdateTenantAdmin(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTenant) return;
    const payload: { email?: string; password?: string } = {};
    if (adminForm.email) payload.email = adminForm.email;
    if (adminForm.password) payload.password = adminForm.password;
    if (!payload.email && !payload.password) {
      setNotice({ type: 'error', message: 'Enter a new email or password.' });
      return;
    }
    try {
      await updateTenantAdmin(selectedTenant.id, payload);
      setEditingAdmin(false);
      setAdminForm({ email: '', password: '' });
      setNotice({ type: 'success', message: 'Tenant admin credentials updated.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to update admin credentials.' });
    }
  }

  async function handleSavePlan(event: React.FormEvent) {
    event.preventDefault();
    if (!planForm) return;
    if (planForm.features.length === 0) {
      setNotice({ type: 'error', message: 'Select at least one feature for this plan.' });
      return;
    }
    const limits = {
      max_devices: parseLimit(planForm.max_devices),
      max_data_streams: parseLimit(planForm.max_data_streams),
      max_users: parseLimit(planForm.max_users),
      max_dashboards: parseLimit(planForm.max_dashboards),
    };
    const commercialFields = {
      price_monthly: parsePrice(planForm.price_monthly),
      currency: planForm.currency.trim() || null,
      is_public: planForm.is_public,
    };
    try {
      if (planForm.id) {
        await updatePlan(planForm.id, {
          name: planForm.name,
          features: planForm.features,
          ...limits,
          ...commercialFields,
        });
      } else {
        const created = await createPlan({
          name: planForm.name,
          slug: planForm.slug,
          features: planForm.features,
          ...limits,
        });
        if (created?.id) {
          await updatePlan(created.id, commercialFields);
        }
      }
      setPlanForm(null);
      await loadData();
      setNotice({ type: 'success', message: 'Plan saved.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to save plan.' });
    }
  }

  async function handleSavePlatformSettings(event: React.FormEvent) {
    event.preventDefault();
    setSavingPlatformSettings(true);
    try {
      const entries: Array<[keyof PlatformSettingsForm, string]> = [
        ['default_trial_days', platformSettingsForm.default_trial_days.trim()],
        ['trial_plan_slug', platformSettingsForm.trial_plan_slug.trim()],
        ['support_email', platformSettingsForm.support_email.trim()],
      ];
      await Promise.all(entries.map(([key, value]) => updatePlatformSetting(key, value)));
      const settings = await getPlatformSettings();
      setPlatformSettings(settings);
      setPlatformSettingsForm({
        default_trial_days: settings.default_trial_days || platformSettingsForm.default_trial_days,
        trial_plan_slug: settings.trial_plan_slug || platformSettingsForm.trial_plan_slug,
        support_email: settings.support_email || platformSettingsForm.support_email,
      });
      setNotice({ type: 'success', message: 'Platform trial and billing settings saved.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to save platform settings.' });
    } finally {
      setSavingPlatformSettings(false);
    }
  }

  async function handleDeletePlan(plan: Plan) {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try {
      await deletePlan(plan.id);
      await loadData();
      setNotice({ type: 'success', message: 'Plan deleted.' });
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to delete plan.' });
    }
  }

  function toggleFeature(feature: string) {
    setPlanForm((prev) => {
      if (!prev) return prev;
      const features = new Set(prev.features);
      if (features.has(feature)) features.delete(feature);
      else features.add(feature);
      return { ...prev, features: Array.from(features) };
    });
  }

  const filteredTenants = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return tenants;
    return tenants.filter((tenant) => (
      tenant.name.toLowerCase().includes(needle)
      || tenant.id.toLowerCase().includes(needle)
      || tenant.plan.toLowerCase().includes(needle)
      || getTenantBasePlanSlug(tenant).toLowerCase().includes(needle)
      || getTenantEffectivePlanSlug(tenant).toLowerCase().includes(needle)
      || formatTenantTrialSummary(tenant).toLowerCase().includes(needle)
    ));
  }, [search, tenants]);

  const activeTenantCount = tenants.filter((tenant) => tenant.is_active).length;
  const suspendedTenantCount = tenants.length - activeTenantCount;
  const isCommandCenter = isCommandCenterTab(activeTab);
  const activeCommandTab = COMMAND_CENTER_TABS.find((item) => item.id === activeTab);
  const selectedPlatformUser = platformUsers.find((account) => account.id === selectedPlatformUserId) || platformUsers[0] || null;
  const selectedBasePlan = selectedTenant ? plans.find((plan) => plan.slug === getTenantBasePlanSlug(selectedTenant)) : undefined;
  const selectedEffectivePlan = selectedTenant ? plans.find((plan) => plan.slug === getTenantEffectivePlanSlug(selectedTenant)) : undefined;

  return (
    <div className="sd-super-shell">
      <aside className={`sd-super-sidebar ${mobileMenuOpen ? 'is-open' : ''}`}>
        <div className="sd-super-brand">
          <div className="sd-brand-mark">S</div>
          <div>
            <div className="sd-brand-name">Seedronics</div>
            <div className="sd-brand-subtitle">Platform Control</div>
          </div>
        </div>

        <nav className="sd-super-nav" aria-label="Super admin navigation">
          {NAV_ITEMS.map((item) => (
            <button
              className={`sd-super-nav-item ${activeTab === item.id ? 'is-active' : ''}`}
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                if (isMobile) setMobileMenuOpen(false);
              }}
              type="button"
            >
              <span className="sd-nav-icon" aria-hidden="true">
                <SuperAdminIcon icon={item.icon} />
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sd-sidebar-footer">
          <div className="sd-user-card">
            <div className="sd-user-avatar">{getInitials(user.email)}</div>
            <div className="sd-user-copy">
              <div className="sd-user-name">{user.email.split('@')[0]}</div>
              <div className="sd-user-role">{user.role}</div>
            </div>
          </div>
          <button className="sd-sign-out" onClick={onLogout} type="button">
            Sign Out
          </button>
        </div>
      </aside>

      {isMobile && mobileMenuOpen && (
        <button
          aria-label="Close menu"
          className="sd-mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          type="button"
        />
      )}

      <main className="sd-super-workspace">
        <AlertToast />

        <header className="sd-super-topbar">
          {isMobile && (
            <button
              aria-label="Open menu"
              className="sd-menu-button"
              onClick={() => setMobileMenuOpen(true)}
              type="button"
            >
              <span />
              <span />
              <span />
            </button>
          )}
          <div className="sd-breadcrumb" aria-label="Current workspace">
            <span>Seedronics Platform</span>
            <span className="sd-breadcrumb-divider">/</span>
            <span>{NAV_ITEMS.find((item) => item.id === activeTab)?.label}</span>
          </div>
          <div className="sd-topbar-actions">
            <div className={`sd-live-pill ${loading ? 'is-stale' : 'is-live'}`}>
              <span />
              {loading ? 'SYNCING' : 'CONTROL LIVE'}
            </div>
            <button className="sd-secondary-button" disabled={loading} onClick={handleRefresh} type="button">
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </header>

        <section className={`sd-page-stack sd-super-page ${isCommandCenter ? 'is-command-center' : ''}`}>
          {isCommandCenter ? (
            <>
              <section className="sd-super-command-header">
                <div className="sd-heading">
                  <div className="sd-eyebrow">SUPER ADMIN</div>
                  <h1>Command Center</h1>
                  <p>{activeCommandTab?.detail || 'Operate users, support, and customer health from one workspace.'}</p>
                </div>
                <div className="sd-super-command-metric-rail" aria-label="Platform command metrics">
                  <CommandCenterMetric label="Active tenants" value={formatNumber(activeTenantCount)} detail={`${suspendedTenantCount} suspended`} tone="success" />
                  <CommandCenterMetric label="Tenant users" value={metrics ? formatNumber(metrics.total_users) : '--'} detail="All workspaces" tone="blue" />
                  <CommandCenterMetric label="Devices" value={metrics ? formatNumber(metrics.total_devices) : '--'} detail="Registered assets" tone="warning" />
                  <CommandCenterMetric label="Readings" value={metrics ? formatNumber(metrics.total_readings) : '--'} detail={`${plans.length} plans configured`} tone="neutral" />
                </div>
              </section>

              <nav className="sd-super-command-tabs" aria-label="Command center sections">
                {COMMAND_CENTER_TABS.map((item) => (
                  <button
                    className={activeTab === item.id ? 'is-active' : ''}
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    type="button"
                  >
                    <span>{item.label}</span>
                    <small>{item.detail}</small>
                  </button>
                ))}
              </nav>
            </>
          ) : (
            <div className="sd-page-header">
              <div className="sd-heading">
                <div className="sd-eyebrow">PLATFORM OPS</div>
                <h1>Super Admin</h1>
                <p>Tenant lifecycle, platform plans, feature gates, and global operating metrics.</p>
              </div>
              <div className="sd-page-actions">
                <span className="sd-status-label is-online">
                  <i />
                  {activeTenantCount} active
                </span>
                {suspendedTenantCount > 0 && (
                  <span className="sd-status-label is-offline">
                    <i />
                    {suspendedTenantCount} suspended
                  </span>
                )}
              </div>
            </div>
          )}

          {notice && (
            <div className={`sd-inline-alert ${notice.type === 'error' ? 'is-danger' : 'is-success'} sd-super-notice`}>
              <span>{notice.message}</span>
              <button onClick={() => setNotice(null)} type="button">Dismiss</button>
            </div>
          )}

          {!isCommandCenter && (
            <section className="sd-kpi-grid sd-super-kpi-grid" aria-label="Platform metrics">
              <StatCard label="Tenants" value={metrics ? formatNumber(metrics.total_tenants) : '--'} detail={`${activeTenantCount} active tenants`} tone="blue" />
              <StatCard label="Users" value={metrics ? formatNumber(metrics.total_users) : '--'} detail="All tenant users" tone="success" />
              <StatCard label="Devices" value={metrics ? formatNumber(metrics.total_devices) : '--'} detail="Registered assets" tone="warning" />
              <StatCard label="Readings" value={metrics ? formatNumber(metrics.total_readings) : '--'} detail={`${plans.length} plans configured`} tone="danger" />
            </section>
          )}

          {activeTab === 'tenants' && (
            <TenantsPanel
              createTenantForm={createTenantForm}
              creatingTenant={creatingTenant}
              defaultTrialDays={getDefaultTrialDays(platformSettingsForm.default_trial_days)}
              filteredTenants={filteredTenants}
              handleActivate={handleActivate}
              handleCreateTenant={handleCreateTenant}
              handleImpersonateClick={handleImpersonateClick}
              handlePlanChange={handlePlanChange}
              handleSuspend={handleSuspend}
              handleTrialToggle={handleTrialToggle}
              plans={plans}
              search={search}
              setCreateTenantForm={setCreateTenantForm}
              setSearch={setSearch}
              setSelectedTenant={setSelectedTenant}
              tenants={tenants}
              trialActionId={trialActionId}
            />
          )}

          {activeTab === 'users' && (
            <PlatformUsersPanel
              alertForm={signupAlertForm}
              alertRecipients={signupAlertRecipients}
              alertRecipientsLoading={signupAlertRecipientsLoading}
              alertAction={signupAlertAction}
              handleResendVerification={handleResendPlatformUserVerification}
              handleResetPassword={handleResetPlatformUserPassword}
              handleSearch={handlePlatformUserSearch}
              handleCreateAlertRecipient={handleCreateSignupAlertRecipient}
              handleDeleteAlertRecipient={handleDeleteSignupAlertRecipient}
              handleSuspend={handleSuspendPlatformUser}
              handleToggleAlertRecipient={handleToggleSignupAlertRecipient}
              handleUnsuspend={handleUnsuspendPlatformUser}
              loading={platformUsersLoading}
              platformUserAction={platformUserAction}
              search={platformUserSearch}
              selectedUser={selectedPlatformUser}
              selectedUserId={selectedPlatformUserId}
              setAlertForm={setSignupAlertForm}
              setSearch={setPlatformUserSearch}
              setSelectedUserId={setSelectedPlatformUserId}
              users={platformUsers}
            />
          )}

          {activeTab === 'plans' && (
            <PlansPanel
              handleDeletePlan={handleDeletePlan}
              handleSavePlatformSettings={handleSavePlatformSettings}
              plans={plans}
              platformSettings={platformSettings}
              savingPlatformSettings={savingPlatformSettings}
              setPlatformSettingsForm={setPlatformSettingsForm}
              setPlanForm={setPlanForm}
              settingsForm={platformSettingsForm}
            />
          )}

          {activeTab === 'metrics' && (
            <MetricsPanel metrics={metrics} plans={plans} tenants={tenants} usage={usage} />
          )}

          {activeTab === 'health' && (
            <SuperAdminTenantHealth
              onImpersonateTenant={handleImpersonateClick}
              onInspectTenant={setSelectedTenant}
              refreshSignal={healthRefreshSignal}
              tenants={tenants}
            />
          )}

          {activeTab === 'support' && (
            <SuperAdminSupport tenants={tenants} />
          )}

          {activeTab === 'audit' && (
            <PlatformAuditPanel
              auditFilters={auditFilters}
              auditLoading={auditLoading}
              auditLogs={auditLogs}
              auditPage={auditPage}
              auditTotal={auditTotal}
              loadAuditLogs={loadAuditLogs}
              setAuditFilters={setAuditFilters}
              tenants={tenants}
            />
          )}

          {activeTab === 'super-admins' && (
            <SuperAdminsPanel
              activateSuperAdmin={handleActivateSuperAdmin}
              changingPassword={changingPassword}
              creatingAdmin={creatingAdmin}
              deactivateSuperAdmin={handleDeactivateSuperAdmin}
              handleChangePassword={handleChangePassword}
              handleCreateSuperAdmin={handleCreateSuperAdmin}
              newAdminEmail={newAdminEmail}
              newAdminPassword={newAdminPassword}
              passwordForm={passwordForm}
              setNewAdminEmail={setNewAdminEmail}
              setNewAdminPassword={setNewAdminPassword}
              setPasswordForm={setPasswordForm}
              superAdmins={superAdmins}
            />
          )}
        </section>
      </main>

      {selectedTenant && (
        <div className="sd-super-modal-overlay" onClick={() => setSelectedTenant(null)}>
          <div className="sd-super-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sd-edit-drawer-header">
              <div>
                <span>Tenant Inspector</span>
                <h2>{selectedTenant.name}</h2>
              </div>
              <button onClick={() => setSelectedTenant(null)} type="button">Close</button>
            </div>
            <div className="sd-super-modal-body">
              <div className="sd-super-detail-grid">
                <Detail label="Tenant ID" value={selectedTenant.id} mono />
                <Detail label="Base Plan" value={selectedBasePlan?.name || formatPlanLabel(getTenantBasePlanSlug(selectedTenant))} />
                <Detail label="Effective Plan" value={selectedEffectivePlan?.name || formatPlanLabel(getTenantEffectivePlanSlug(selectedTenant))} />
                <Detail label="Trial" value={formatTenantTrialSummary(selectedTenant)} />
                <Detail label="Status" value={selectedTenant.is_active ? 'Active' : 'Suspended'} />
                <Detail label="Devices" value={String(selectedTenant.device_count)} />
                <Detail label="Users" value={String(selectedTenant.user_count)} />
                <Detail label="Alerts" value={String(selectedTenant.alert_count)} />
                <Detail label="Created" value={formatDateTime(selectedTenant.created_at)} />
                {selectedTenant.trial_ends_at && (
                  <Detail label="Trial Ends" value={formatDateTime(selectedTenant.trial_ends_at)} />
                )}
                {selectedTenant.suspended_at && (
                  <Detail label="Suspended" value={formatDateTime(selectedTenant.suspended_at)} />
                )}
              </div>

              <section className="sd-super-admin-editor sd-super-trial-editor">
                <div className="sd-panel-header">
                  <div>
                    <h2>Trial Control</h2>
                    <p>Activate, extend, or deactivate trial access without changing the tenant's stored plan.</p>
                  </div>
                  <TenantTrialStatus tenant={selectedTenant} />
                </div>
                <form className="sd-super-form-grid" onSubmit={handleSaveTenantTrial}>
                  <label className="sd-field">
                    <span>Trial ends</span>
                    <input
                      min={formatTrialDateInput(new Date().toISOString(), 0)}
                      onChange={(event) => setTrialEndAt(event.target.value)}
                      required
                      type="datetime-local"
                      value={trialEndAt}
                    />
                    <small>Use the tenant's local business date/time. The backend stores the exact ISO timestamp.</small>
                  </label>
                  <div className="sd-super-trial-actions">
                    <button className="sd-primary-button" disabled={trialActionId === selectedTenant.id} type="submit">
                      {trialActionId === selectedTenant.id ? 'Saving...' : selectedTenant.is_trial ? 'Extend Trial' : 'Activate Trial'}
                    </button>
                    <button
                      className="sd-settings-danger-button"
                      disabled={trialActionId === selectedTenant.id || !selectedTenant.trial_ends_at}
                      onClick={handleClearTenantTrial}
                      type="button"
                    >
                      Deactivate Trial
                    </button>
                  </div>
                </form>
              </section>

              <section className="sd-super-admin-editor">
                <div className="sd-panel-header">
                  <div>
                    <h2>Default Admin</h2>
                    <p>Update the default tenant administrator credentials.</p>
                  </div>
                  <button className="sd-secondary-button" onClick={() => setEditingAdmin((value) => !value)} type="button">
                    {editingAdmin ? 'Cancel' : 'Edit Admin'}
                  </button>
                </div>
                {editingAdmin ? (
                  <form className="sd-super-form-grid" onSubmit={handleUpdateTenantAdmin}>
                    <label className="sd-field">
                      <span>Email</span>
                      <input
                        onChange={(event) => setAdminForm((prev) => ({ ...prev, email: event.target.value }))}
                        placeholder="e.g. admin@tenant.com"
                        type="email"
                        value={adminForm.email}
                      />
                      <small>Leave unchanged if only rotating the password.</small>
                    </label>
                    <label className="sd-field">
                      <span>Password</span>
                      <input
                        onChange={(event) => setAdminForm((prev) => ({ ...prev, password: event.target.value }))}
                        placeholder="Enter a new temporary password"
                        type="password"
                        value={adminForm.password}
                      />
                      <small>Leave blank if only updating the email address.</small>
                    </label>
                    <button className="sd-primary-button sd-form-span-2" type="submit">Save Admin Credentials</button>
                  </form>
                ) : (
                  <p className="sd-super-helper">Use Edit Admin to change the tenant admin email or password without touching tenant data.</p>
                )}
              </section>

              <section className="sd-super-admin-editor">
                <div className="sd-panel-header">
                  <div>
                    <h2>Tenant Admins</h2>
                    <p>Manage every administrator account assigned to this tenant.</p>
                  </div>
                </div>
                <form className="sd-super-form-grid" onSubmit={handleAddTenantAdmin}>
                  <label className="sd-field">
                    <span>Email</span>
                    <input
                      onChange={(event) => setAddAdminForm((prev) => ({ ...prev, email: event.target.value }))}
                      placeholder="admin@tenant.com"
                      required
                      type="email"
                      value={addAdminForm.email}
                    />
                    <small>Email for the new administrator account inside this tenant.</small>
                  </label>
                  <label className="sd-field">
                    <span>Password</span>
                    <input
                      onChange={(event) => setAddAdminForm((prev) => ({ ...prev, password: event.target.value }))}
                      placeholder="Temporary password"
                      required
                      type="password"
                      value={addAdminForm.password}
                    />
                    <small>Temporary password the admin should change after first login.</small>
                  </label>
                  <button className="sd-primary-button sd-form-span-2" disabled={addingTenantAdmin} type="submit">
                    {addingTenantAdmin ? 'Adding...' : 'Add Tenant Admin'}
                  </button>
                </form>
                <div className="sd-super-tenant-admin-list">
                  {tenantAdmins.length === 0 ? (
                    <div className="sd-mobile-empty-card">No tenant admins found.</div>
                  ) : (
                    tenantAdmins.map((admin) => (
                      <article className="sd-super-admin-row" key={admin.id}>
                        {editingTenantAdmin?.id === admin.id ? (
                          <form className="sd-super-inline-edit" onSubmit={handleSaveTenantAdmin}>
                            <input
                              onChange={(event) => setEditingTenantAdmin((prev) => (prev ? { ...prev, email: event.target.value } : prev))}
                              placeholder="New email"
                              type="email"
                              value={editingTenantAdmin.email}
                            />
                            <input
                              onChange={(event) => setEditingTenantAdmin((prev) => (prev ? { ...prev, password: event.target.value } : prev))}
                              placeholder="New password"
                              type="password"
                              value={editingTenantAdmin.password}
                            />
                            <button type="submit">Save</button>
                            <button onClick={() => setEditingTenantAdmin(null)} type="button">Cancel</button>
                          </form>
                        ) : (
                          <>
                            <div>
                              <strong>{admin.email}</strong>
                              <span>{admin.role} | {formatDate(admin.created_at)}</span>
                            </div>
                            <div className="sd-row-actions">
                              <span className={`sd-status-label ${admin.is_active ? 'is-online' : 'is-offline'}`}>
                                <i />
                                {admin.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <button
                                onClick={() => setEditingTenantAdmin({ id: admin.id, email: admin.email, password: '' })}
                                type="button"
                              >
                                Edit
                              </button>
                            </div>
                          </>
                        )}
                      </article>
                    ))
                  )}
                </div>
              </section>

              <div className="sd-drawer-actions">
                <button onClick={() => setSelectedTenant(null)} type="button">Close</button>
                <button onClick={() => handleImpersonateClick(selectedTenant)} type="button">Login as Tenant</button>
                {selectedTenant.is_active ? (
                  <button className="is-danger" onClick={() => handleSuspend(selectedTenant.id)} type="button">
                    Suspend Tenant
                  </button>
                ) : (
                  <button onClick={() => handleActivate(selectedTenant.id)} type="button">
                    Activate Tenant
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {planForm && (
        <div className="sd-super-modal-overlay" onClick={() => setPlanForm(null)}>
          <div className="sd-super-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sd-edit-drawer-header">
              <div>
                <span>Plan Builder</span>
                <h2>{planForm.id ? 'Edit Plan' : 'New Plan'}</h2>
              </div>
              <button onClick={() => setPlanForm(null)} type="button">Close</button>
            </div>
            <form className="sd-super-modal-body" onSubmit={handleSavePlan}>
              <div className="sd-super-form-grid">
                <label className="sd-field">
                  <span>Name</span>
                  <input
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))}
                    placeholder="e.g. Enterprise"
                    required
                    type="text"
                    value={planForm.name}
                  />
                  <small>Human-readable plan name shown to admins and tenants.</small>
                </label>
                <label className="sd-field">
                  <span>Slug</span>
                  <input
                    disabled={!!planForm.id}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, slug: event.target.value } : prev))}
                    placeholder="e.g. enterprise"
                    required
                    type="text"
                    value={planForm.slug}
                  />
                  <small>{planForm.id ? 'Plan slugs are fixed after creation to protect tenant assignments.' : 'Stable lowercase identifier used by the backend and tenant assignments.'}</small>
                </label>
                <label className="sd-field">
                  <span>Max Devices</span>
                  <input
                    min={0}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, max_devices: event.target.value } : prev))}
                    placeholder="Blank for unlimited"
                    type="number"
                    value={planForm.max_devices}
                  />
                  <small>Maximum devices allowed. Leave blank for unlimited capacity.</small>
                </label>
                <label className="sd-field">
                  <span>Max Data Streams</span>
                  <input
                    min={0}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, max_data_streams: event.target.value } : prev))}
                    placeholder="Blank for unlimited"
                    type="number"
                    value={planForm.max_data_streams}
                  />
                  <small>Maximum data streams (metric keys) across all devices. Leave blank for unlimited.</small>
                </label>
                <label className="sd-field">
                  <span>Max Users</span>
                  <input
                    min={0}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, max_users: event.target.value } : prev))}
                    placeholder="Blank for unlimited"
                    type="number"
                    value={planForm.max_users}
                  />
                  <small>Maximum tenant users allowed. Leave blank for unlimited seats.</small>
                </label>
                <label className="sd-field">
                  <span>Max Dashboards</span>
                  <input
                    min={0}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, max_dashboards: event.target.value } : prev))}
                    placeholder="Blank for unlimited"
                    type="number"
                    value={planForm.max_dashboards}
                  />
                  <small>Maximum saved dashboards allowed. Leave blank for unlimited workspaces.</small>
                </label>
                <label className="sd-field">
                  <span>Monthly Price</span>
                  <input
                    min={0}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, price_monthly: event.target.value } : prev))}
                    placeholder="Blank for custom pricing"
                    step="0.01"
                    type="number"
                    value={planForm.price_monthly}
                  />
                  <small>Shown on the landing pricing page and upgrade modal.</small>
                </label>
                <label className="sd-field">
                  <span>Currency</span>
                  <input
                    maxLength={10}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, currency: event.target.value.toUpperCase() } : prev))}
                    placeholder="USD"
                    type="text"
                    value={planForm.currency}
                  />
                  <small>Use a currency code such as USD, NGN, GBP, or EUR.</small>
                </label>
              </div>
              <div className="sd-super-feature-grid is-compact">
                <label className="sd-checkbox-card">
                  <input
                    checked={planForm.is_public}
                    onChange={(event) => setPlanForm((prev) => (prev ? { ...prev, is_public: event.target.checked } : prev))}
                    type="checkbox"
                  />
                  <span>
                    <strong>Show on public pricing</strong>
                    <small>Public plans appear in `/pricing` and the tenant upgrade modal.</small>
                  </span>
                </label>
              </div>
              <div className="sd-super-feature-grid">
                {ALL_FEATURES.map((feature) => (
                  <label className="sd-checkbox-card" key={feature.id}>
                    <input
                      checked={planForm.features.includes(feature.id)}
                      onChange={() => toggleFeature(feature.id)}
                      type="checkbox"
                    />
                    <span>
                      <strong>{feature.label}</strong>
                      <small>{feature.id}</small>
                    </span>
                  </label>
                ))}
              </div>
              <div className="sd-drawer-actions">
                <button onClick={() => setPlanForm(null)} type="button">Cancel</button>
                <button type="submit">Save Plan</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TenantsPanel({
  createTenantForm,
  creatingTenant,
  defaultTrialDays,
  filteredTenants,
  handleActivate,
  handleCreateTenant,
  handleImpersonateClick,
  handlePlanChange,
  handleSuspend,
  handleTrialToggle,
  plans,
  search,
  setCreateTenantForm,
  setSearch,
  setSelectedTenant,
  tenants,
  trialActionId,
}: {
  createTenantForm: { tenant_name: string; admin_email: string; admin_password: string; plan: string };
  creatingTenant: boolean;
  defaultTrialDays: number;
  filteredTenants: Tenant[];
  handleActivate: (tenantId: string) => void;
  handleCreateTenant: (event: React.FormEvent) => void;
  handleImpersonateClick: (tenant: Tenant) => void;
  handlePlanChange: (tenantId: string, plan: string) => void;
  handleSuspend: (tenantId: string) => void;
  handleTrialToggle: (tenant: Tenant) => void;
  plans: Plan[];
  search: string;
  setCreateTenantForm: React.Dispatch<React.SetStateAction<{ tenant_name: string; admin_email: string; admin_password: string; plan: string }>>;
  setSearch: (value: string) => void;
  setSelectedTenant: (tenant: Tenant) => void;
  tenants: Tenant[];
  trialActionId: string;
}) {
  return (
    <section className="sd-page-stack">
      <article className="sd-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Create Tenant</h2>
            <p>Provision a tenant with a default administrator and an assigned feature plan.</p>
          </div>
        </div>
        <form className="sd-super-form-grid sd-super-create-tenant" onSubmit={handleCreateTenant}>
          <label className="sd-field">
            <span>Tenant Name</span>
            <input
              onChange={(event) => setCreateTenantForm((prev) => ({ ...prev, tenant_name: event.target.value }))}
              placeholder="Port Harcourt Terminal"
              required
              type="text"
              value={createTenantForm.tenant_name}
            />
            <small>Customer or workspace name users will see after login.</small>
          </label>
          <label className="sd-field">
            <span>Admin Email</span>
            <input
              onChange={(event) => setCreateTenantForm((prev) => ({ ...prev, admin_email: event.target.value }))}
              placeholder="admin@company.com"
              required
              type="email"
              value={createTenantForm.admin_email}
            />
            <small>Default administrator email created for this tenant.</small>
          </label>
          <label className="sd-field">
            <span>Admin Password</span>
            <input
              onChange={(event) => setCreateTenantForm((prev) => ({ ...prev, admin_password: event.target.value }))}
              placeholder="Temporary password"
              required
              type="password"
              value={createTenantForm.admin_password}
            />
            <small>Temporary password for the first admin login.</small>
          </label>
          <label className="sd-field">
            <span>Plan</span>
            <select
              onChange={(event) => setCreateTenantForm((prev) => ({ ...prev, plan: event.target.value }))}
              value={createTenantForm.plan}
            >
              {plans.map((plan) => (
                <option key={plan.slug} value={plan.slug}>{plan.name}</option>
              ))}
            </select>
            <small>Selected plan controls which tenant modules are visible.</small>
          </label>
          <button className="sd-primary-button" disabled={creatingTenant} type="submit">
            {creatingTenant ? 'Creating...' : 'Create Tenant'}
          </button>
        </form>
      </article>

      <article className="sd-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Tenant Registry</h2>
            <p>{filteredTenants.length} of {tenants.length} tenants in scope.</p>
          </div>
          <label className="sd-super-search">
            <span>Search</span>
            <input
              onChange={(event) => setSearch(event.target.value)}
              placeholder="tenant name, id, or plan..."
              type="search"
              value={search}
            />
          </label>
        </div>
        <div className="sd-table-wrap sd-super-tenant-table-wrap">
          <table className="sd-table sd-super-tenant-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Plan</th>
                <th>Trial</th>
                <th>Status</th>
                <th>Devices</th>
                <th>Users</th>
                <th>Alerts</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.length === 0 ? (
                <tr><td className="sd-empty-row" colSpan={9}>No tenants match this filter.</td></tr>
              ) : (
                filteredTenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td>
                      <strong>{tenant.name}</strong>
                      <span className="sd-mono">{tenant.id.slice(0, 8)}</span>
                    </td>
                    <td>
                      <select value={getTenantBasePlanSlug(tenant)} onChange={(event) => handlePlanChange(tenant.id, event.target.value)}>
                        {plans.map((plan) => (
                          <option key={plan.slug} value={plan.slug}>{plan.name}</option>
                        ))}
                      </select>
                      {tenant.is_trial && (
                        <small className="sd-super-plan-note">Effective: {formatPlanLabel(getTenantEffectivePlanSlug(tenant))}</small>
                      )}
                    </td>
                    <td><TenantTrialStatus tenant={tenant} /></td>
                    <td><TenantStatus tenant={tenant} /></td>
                    <td>{tenant.device_count}</td>
                    <td>{tenant.user_count}</td>
                    <td>{tenant.alert_count}</td>
                    <td>{formatDate(tenant.created_at)}</td>
                    <td>
                      <div className="sd-row-actions">
                        {tenant.is_active ? (
                          <button className="is-danger" onClick={() => handleSuspend(tenant.id)} type="button">Suspend</button>
                        ) : (
                          <button onClick={() => handleActivate(tenant.id)} type="button">Activate</button>
                        )}
                        <button disabled={trialActionId === tenant.id} onClick={() => handleTrialToggle(tenant)} type="button">
                          {trialActionId === tenant.id
                            ? 'Saving...'
                            : tenant.is_trial
                              ? 'End trial'
                              : `Start ${defaultTrialDays}d trial`}
                        </button>
                        <button onClick={() => handleImpersonateClick(tenant)} type="button">Login as</button>
                        <button onClick={() => setSelectedTenant(tenant)} type="button">Details</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="sd-super-tenant-card-list">
          {filteredTenants.length === 0 ? (
            <div className="sd-mobile-empty-card">No tenants match this filter.</div>
          ) : (
            filteredTenants.map((tenant) => (
              <article className="sd-mobile-record-card" key={tenant.id}>
                <header className="sd-mobile-record-header">
                  <TenantStatus tenant={tenant} />
                  <time>{formatDate(tenant.created_at)}</time>
                </header>
                <div className="sd-mobile-record-title">
                  <strong>{tenant.name}</strong>
                  <span className="sd-mono">{tenant.id}</span>
                </div>
                <div className="sd-mobile-record-grid">
                  <div className="sd-mobile-record-fact">
                    <span>Plan</span>
                    <select value={getTenantBasePlanSlug(tenant)} onChange={(event) => handlePlanChange(tenant.id, event.target.value)}>
                      {plans.map((plan) => (
                        <option key={plan.slug} value={plan.slug}>{plan.name}</option>
                      ))}
                    </select>
                    {tenant.is_trial && <small>Effective: {formatPlanLabel(getTenantEffectivePlanSlug(tenant))}</small>}
                  </div>
                  <div className="sd-mobile-record-fact">
                    <span>Assets</span>
                    <strong>{tenant.device_count} devices / {tenant.alert_count} alerts</strong>
                  </div>
                  <div className="sd-mobile-record-fact">
                    <span>Trial</span>
                    <TenantTrialStatus tenant={tenant} />
                  </div>
                  <div className="sd-mobile-record-fact">
                    <span>Users</span>
                    <strong>{tenant.user_count}</strong>
                  </div>
                </div>
                <div className="sd-mobile-record-actions">
                  {tenant.is_active ? (
                    <button className="is-danger" onClick={() => handleSuspend(tenant.id)} type="button">Suspend</button>
                  ) : (
                    <button onClick={() => handleActivate(tenant.id)} type="button">Activate</button>
                  )}
                  <button disabled={trialActionId === tenant.id} onClick={() => handleTrialToggle(tenant)} type="button">
                    {trialActionId === tenant.id
                      ? 'Saving...'
                      : tenant.is_trial
                        ? 'End trial'
                        : `Start ${defaultTrialDays}d trial`}
                  </button>
                  <button onClick={() => handleImpersonateClick(tenant)} type="button">Login as</button>
                  <button onClick={() => setSelectedTenant(tenant)} type="button">Details</button>
                </div>
              </article>
            ))
          )}
        </div>
      </article>
    </section>
  );
}

function PlatformUsersPanel({
  alertAction,
  alertForm,
  alertRecipients,
  alertRecipientsLoading,
  handleCreateAlertRecipient,
  handleDeleteAlertRecipient,
  handleResendVerification,
  handleResetPassword,
  handleSearch,
  handleSuspend,
  handleToggleAlertRecipient,
  handleUnsuspend,
  loading,
  platformUserAction,
  search,
  selectedUser,
  selectedUserId,
  setAlertForm,
  setSearch,
  setSelectedUserId,
  users,
}: {
  alertAction: string;
  alertForm: { email: string; label: string; enabled: boolean };
  alertRecipients: SignupAlertRecipient[];
  alertRecipientsLoading: boolean;
  handleCreateAlertRecipient: (event: React.FormEvent) => void;
  handleDeleteAlertRecipient: (recipient: SignupAlertRecipient) => void;
  handleResendVerification: (user: SuperAdminUser) => void;
  handleResetPassword: (user: SuperAdminUser) => void;
  handleSearch: (event: React.FormEvent) => void;
  handleSuspend: (user: SuperAdminUser) => void;
  handleToggleAlertRecipient: (recipient: SignupAlertRecipient) => void;
  handleUnsuspend: (user: SuperAdminUser) => void;
  loading: boolean;
  platformUserAction: string;
  search: string;
  selectedUser: SuperAdminUser | null;
  selectedUserId: string;
  setAlertForm: (value: { email: string; label: string; enabled: boolean }) => void;
  setSearch: (value: string) => void;
  setSelectedUserId: (value: string) => void;
  users: SuperAdminUser[];
}) {
  return (
    <section className="sd-page-stack sd-platform-users-command">
      <div className="sd-super-user-command-grid">
        <article className="sd-panel sd-super-user-directory">
          <div className="sd-panel-header sd-command-directory-header">
            <div>
              <h2>User Directory</h2>
              <p>{users.length} account{users.length === 1 ? '' : 's'} in the current view.</p>
            </div>
            <form className="sd-super-search" onSubmit={handleSearch}>
              <span>Search</span>
              <input
                onChange={(event) => setSearch(event.target.value)}
                placeholder="email, name, or tenant..."
                type="search"
                value={search}
              />
              <button disabled={loading} type="submit">{loading ? 'Loading' : 'Search'}</button>
            </form>
          </div>

          <div className="sd-table-wrap sd-super-user-table-wrap">
            <table className="sd-table sd-super-user-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Workspace</th>
                  <th>Status</th>
                  <th>Verified</th>
                  <th>Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td className="sd-empty-row" colSpan={6}>{loading ? 'Loading users...' : 'No users match this filter.'}</td></tr>
                ) : (
                  users.map((account) => (
                    <tr
                      className={selectedUserId === account.id ? 'is-selected' : ''}
                      key={account.id}
                      onClick={() => setSelectedUserId(account.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedUserId(account.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <td>
                        <strong>{account.full_name || account.email}</strong>
                        <span>{account.email}</span>
                        {account.phone && <em>{account.phone}</em>}
                      </td>
                      <td>
                        <UserWorkspaceSummary account={account} />
                      </td>
                      <td><UserLifecycleStatus account={account} /></td>
                      <td><VerificationStatusBadge account={account} /></td>
                      <td>{formatDateTime(account.last_login_at || undefined)}</td>
                      <td>
                        <div className="sd-row-actions sd-super-user-actions" onClick={(event) => event.stopPropagation()}>
                          {account.suspended_by_platform ? (
                            <button disabled={platformUserAction === account.id} onClick={() => handleUnsuspend(account)} type="button">Unsuspend</button>
                          ) : (
                            <button className="is-danger" disabled={platformUserAction === account.id} onClick={() => handleSuspend(account)} type="button">Suspend</button>
                          )}
                          <button disabled={platformUserAction === account.id} onClick={() => handleResetPassword(account)} type="button">Reset</button>
                          <button disabled={platformUserAction === account.id || !!account.email_verified_at} onClick={() => handleResendVerification(account)} type="button">Verify</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="sd-super-user-card-list">
            {users.length === 0 ? (
              <div className="sd-mobile-empty-card">{loading ? 'Loading users...' : 'No users match this filter.'}</div>
            ) : (
              users.map((account) => (
                <article className={`sd-mobile-record-card ${selectedUserId === account.id ? 'is-selected' : ''}`} key={account.id}>
                  <header className="sd-mobile-record-header">
                    <UserLifecycleStatus account={account} />
                    <time>{formatDate(account.created_at)}</time>
                  </header>
                  <div className="sd-mobile-record-title">
                    <strong>{account.full_name || account.email}</strong>
                    <span>{account.email}</span>
                    {account.phone && <span>{account.phone}</span>}
                  </div>
                  <div className="sd-mobile-record-grid">
                    <div className="sd-mobile-record-fact">
                      <span>Verification</span>
                      <VerificationStatusBadge account={account} />
                    </div>
                    <div className="sd-mobile-record-fact">
                      <span>Source</span>
                      <strong>{formatAuditValue(account.signup_source || 'admin')}</strong>
                    </div>
                    <div className="sd-mobile-record-fact">
                      <span>Workspaces</span>
                      <strong>{account.memberships.length}</strong>
                    </div>
                    <div className="sd-mobile-record-fact">
                      <span>Last Login</span>
                      <strong>{formatDateTime(account.last_login_at || undefined)}</strong>
                    </div>
                  </div>
                  <div className="sd-mobile-record-actions">
                    <button onClick={() => setSelectedUserId(account.id)} type="button">Inspect</button>
                    {account.suspended_by_platform ? (
                      <button disabled={platformUserAction === account.id} onClick={() => handleUnsuspend(account)} type="button">Unsuspend</button>
                    ) : (
                      <button className="is-danger" disabled={platformUserAction === account.id} onClick={() => handleSuspend(account)} type="button">Suspend</button>
                    )}
                    <button disabled={platformUserAction === account.id} onClick={() => handleResetPassword(account)} type="button">Reset password</button>
                    <button disabled={platformUserAction === account.id || !!account.email_verified_at} onClick={() => handleResendVerification(account)} type="button">Resend verification</button>
                  </div>
                </article>
              ))
            )}
          </div>

          <details className="sd-command-secondary-drawer">
            <summary>
              <span>Signup Alerts</span>
              <small>{alertRecipients.length} recipient{alertRecipients.length === 1 ? '' : 's'} configured</small>
            </summary>
            <SignupAlertRecipientsPanel
              action={alertAction}
              form={alertForm}
              loading={alertRecipientsLoading}
              onCreate={handleCreateAlertRecipient}
              onDelete={handleDeleteAlertRecipient}
              onFormChange={setAlertForm}
              onToggle={handleToggleAlertRecipient}
              recipients={alertRecipients}
            />
          </details>
        </article>

        <PlatformUserInspector
          account={selectedUser}
          handleResendVerification={handleResendVerification}
          handleResetPassword={handleResetPassword}
          handleSuspend={handleSuspend}
          handleUnsuspend={handleUnsuspend}
          platformUserAction={platformUserAction}
        />
      </div>
    </section>
  );
}

function UserWorkspaceSummary({ account }: { account: SuperAdminUser }) {
  const firstMembership = account.memberships[0];
  if (!firstMembership) {
    return <span className="sd-super-muted">No tenant memberships</span>;
  }

  const remainingCount = account.memberships.length - 1;

  return (
    <div className="sd-user-workspace-summary">
      <strong>{firstMembership.tenant_name}</strong>
      <span>{formatAuditValue(firstMembership.role)} / {formatPlanLabel(firstMembership.plan || undefined)}</span>
      {remainingCount > 0 && <small>+{remainingCount} more workspace{remainingCount === 1 ? '' : 's'}</small>}
    </div>
  );
}

function PlatformUserInspector({
  account,
  handleResendVerification,
  handleResetPassword,
  handleSuspend,
  handleUnsuspend,
  platformUserAction,
}: {
  account: SuperAdminUser | null;
  handleResendVerification: (user: SuperAdminUser) => void;
  handleResetPassword: (user: SuperAdminUser) => void;
  handleSuspend: (user: SuperAdminUser) => void;
  handleUnsuspend: (user: SuperAdminUser) => void;
  platformUserAction: string;
}) {
  if (!account) {
    return (
      <aside className="sd-panel sd-super-user-inspector">
        <div className="sd-command-panel-header">
          <div>
            <h2>User Inspector</h2>
            <p>Select a directory row to review profile, workspaces, and controls.</p>
          </div>
        </div>
        <div className="sd-command-empty">
          <strong>No user selected</strong>
          <p>Search or select a user to inspect account context.</p>
        </div>
      </aside>
    );
  }

  const primaryMembership = account.memberships[0];

  return (
    <aside className="sd-panel sd-super-user-inspector">
      <header className="sd-super-user-inspector-header">
        <div className="sd-super-inspector-avatar">{getInitials(account.full_name || account.email)}</div>
        <div>
          <h2>{account.full_name || account.email}</h2>
          <p>{account.email}</p>
          {account.phone && <p>{account.phone}</p>}
        </div>
      </header>

      <div className="sd-super-user-inspector-badges">
        <UserLifecycleStatus account={account} />
        <VerificationStatusBadge account={account} />
      </div>

      <div className="sd-super-user-inspector-stats">
        <Detail label="Workspaces" value={formatNumber(account.memberships.length)} />
        <Detail label="Role" value={formatAuditValue(account.role || 'member')} />
        <Detail label="Source" value={formatAuditValue(account.signup_source || 'admin')} />
      </div>

      <div className="sd-command-context-list">
        <div>
          <span>Last login</span>
          <strong>{formatDateTime(account.last_login_at || undefined)}</strong>
        </div>
        <div>
          <span>Created</span>
          <strong>{formatDate(account.created_at)}</strong>
        </div>
        <div>
          <span>User ID</span>
          <strong className="sd-mono">{account.id}</strong>
        </div>
      </div>

      {primaryMembership && (
        <section className="sd-super-inspector-focus">
          <div className="sd-command-panel-header">
            <div>
              <h2>Primary Workspace</h2>
              <p>{primaryMembership.tenant_name}</p>
            </div>
            <span className="sd-state-chip">{formatPlanLabel(primaryMembership.plan || undefined)}</span>
          </div>
          <div className="sd-super-signup-profile-grid">
            <Detail label="Industry" value={primaryMembership.industry || '-'} />
            <Detail label="Country" value={primaryMembership.country || '-'} />
            <Detail label="Company size" value={primaryMembership.company_size || '-'} />
            <Detail label="Trial ends" value={formatDate(primaryMembership.trial_ends_at || undefined)} />
          </div>
        </section>
      )}

      <section className="sd-super-inspector-memberships">
        <div className="sd-command-panel-header">
          <div>
            <h2>All Workspaces</h2>
            <p>{account.memberships.length || 'No'} tenant membership{account.memberships.length === 1 ? '' : 's'}.</p>
          </div>
        </div>
        <SignupMembershipStack account={account} />
      </section>

      <div className="sd-command-action-grid">
        {account.suspended_by_platform ? (
          <button disabled={platformUserAction === account.id} onClick={() => handleUnsuspend(account)} type="button">Unsuspend</button>
        ) : (
          <button className="is-danger" disabled={platformUserAction === account.id} onClick={() => handleSuspend(account)} type="button">Suspend</button>
        )}
        <button disabled={platformUserAction === account.id} onClick={() => handleResetPassword(account)} type="button">Reset Password</button>
        <button disabled={platformUserAction === account.id || !!account.email_verified_at} onClick={() => handleResendVerification(account)} type="button">Resend Verification</button>
      </div>
    </aside>
  );
}

function SignupMembershipStack({ account }: { account: SuperAdminUser }) {
  if (account.memberships.length === 0) {
    return <span className="sd-super-muted">No tenant memberships</span>;
  }

  return (
    <div className="sd-super-membership-stack">
      {account.memberships.map((membership) => {
        const profileBits = [
          membership.industry,
          membership.country,
          membership.company_size,
        ].filter(Boolean);

        return (
          <article className="sd-super-membership-card" key={`${account.id}.${membership.tenant_id}`}>
            <header>
              <div>
                <strong>{membership.tenant_name}</strong>
                <span>{membership.role}</span>
              </div>
              <b>{membership.plan || 'No plan'}</b>
            </header>

            <div className="sd-super-signup-profile-grid">
              <Detail label="Industry" value={membership.industry || '-'} />
              <Detail label="Country" value={membership.country || '-'} />
              <Detail label="Company size" value={membership.company_size || '-'} />
              <Detail label="Trial ends" value={formatDate(membership.trial_ends_at || undefined)} />
            </div>

            {profileBits.length > 0 && (
              <p className="sd-super-profile-line">{profileBits.join(' / ')}</p>
            )}

            {membership.description && (
              <p className="sd-super-signup-description">{membership.description}</p>
            )}
          </article>
        );
      })}
    </div>
  );
}

function SignupAlertRecipientsPanel({
  action,
  form,
  loading,
  onCreate,
  onDelete,
  onFormChange,
  onToggle,
  recipients,
}: {
  action: string;
  form: { email: string; label: string; enabled: boolean };
  loading: boolean;
  onCreate: (event: React.FormEvent) => void;
  onDelete: (recipient: SignupAlertRecipient) => void;
  onFormChange: (value: { email: string; label: string; enabled: boolean }) => void;
  onToggle: (recipient: SignupAlertRecipient) => void;
  recipients: SignupAlertRecipient[];
}) {
  return (
    <article className="sd-panel">
      <div className="sd-panel-header">
        <div>
          <h2>Signup Alerts</h2>
          <p>Manage the emails that receive a notification whenever a new company signs up.</p>
        </div>
      </div>

      <form className="sd-super-signup-alert-form" onSubmit={onCreate}>
        <label>
          Alert email
          <input
            autoComplete="email"
            onChange={(event) => onFormChange({ ...form, email: event.target.value })}
            placeholder="admin@seedronics.com"
            required
            type="email"
            value={form.email}
          />
        </label>
        <label>
          Label
          <input
            onChange={(event) => onFormChange({ ...form, label: event.target.value })}
            placeholder="Sales, support, founder..."
            type="text"
            value={form.label}
          />
        </label>
        <label className="sd-super-checkbox">
          <input
            checked={form.enabled}
            onChange={(event) => onFormChange({ ...form, enabled: event.target.checked })}
            type="checkbox"
          />
          Enabled
        </label>
        <button className="sd-primary-button" disabled={action === 'create'} type="submit">
          {action === 'create' ? 'Adding...' : 'Add recipient'}
        </button>
      </form>

      <div className="sd-super-recipient-list">
        {recipients.length === 0 ? (
          <div className="sd-mobile-empty-card">{loading ? 'Loading alert recipients...' : 'No signup alert recipients yet.'}</div>
        ) : (
          recipients.map((recipient) => (
            <article className="sd-super-recipient-row" key={recipient.id}>
              <div>
                <strong>{recipient.email}</strong>
                <span>{recipient.label || 'Signup alert recipient'}</span>
                <small>Updated {formatDateTime(recipient.updated_at)}</small>
              </div>
              <div className="sd-row-actions">
                <span className={`sd-status-label ${recipient.enabled ? 'is-online' : 'is-maintenance'}`}>
                  <i />
                  {recipient.enabled ? 'Enabled' : 'Paused'}
                </span>
                <button disabled={action === recipient.id} onClick={() => onToggle(recipient)} type="button">
                  {recipient.enabled ? 'Pause' : 'Enable'}
                </button>
                <button className="is-danger" disabled={action === recipient.id} onClick={() => onDelete(recipient)} type="button">
                  Remove
                </button>
              </div>
            </article>
          ))
        )}
      </div>
    </article>
  );
}

function PlansPanel({
  handleDeletePlan,
  handleSavePlatformSettings,
  plans,
  platformSettings,
  savingPlatformSettings,
  setPlatformSettingsForm,
  setPlanForm,
  settingsForm,
}: {
  handleDeletePlan: (plan: Plan) => void;
  handleSavePlatformSettings: (event: React.FormEvent) => void;
  plans: Plan[];
  platformSettings: PlatformSettings;
  savingPlatformSettings: boolean;
  setPlatformSettingsForm: React.Dispatch<React.SetStateAction<PlatformSettingsForm>>;
  setPlanForm: React.Dispatch<React.SetStateAction<PlanFormState | null>>;
  settingsForm: PlatformSettingsForm;
}) {
  return (
    <section className="sd-page-stack">
      <article className="sd-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Trial & Billing Settings</h2>
            <p>Defaults used by self-service signup, trial countdowns, and email fallback upgrade requests.</p>
          </div>
        </div>
        <form className="sd-super-settings-form" onSubmit={handleSavePlatformSettings}>
          <label className="sd-field">
            <span>Default Trial Days</span>
            <input
              min={1}
              onChange={(event) => setPlatformSettingsForm((prev) => ({ ...prev, default_trial_days: event.target.value }))}
              required
              type="number"
              value={settingsForm.default_trial_days}
            />
            <small>Current backend value: {platformSettings.default_trial_days || '30'} days.</small>
          </label>
          <label className="sd-field">
            <span>Trial Plan</span>
            <select
              onChange={(event) => setPlatformSettingsForm((prev) => ({ ...prev, trial_plan_slug: event.target.value }))}
              value={settingsForm.trial_plan_slug}
            >
              {plans.map((plan) => (
                <option key={plan.slug} value={plan.slug}>{plan.name}</option>
              ))}
            </select>
            <small>New signups evaluate this plan during their trial.</small>
          </label>
          <label className="sd-field">
            <span>Support Email</span>
            <input
              onChange={(event) => setPlatformSettingsForm((prev) => ({ ...prev, support_email: event.target.value }))}
              placeholder="admin@seedronics.com"
              required
              type="email"
              value={settingsForm.support_email}
            />
            <small>Receives upgrade requests when no payment gateway URL is configured.</small>
          </label>
          <button className="sd-primary-button" disabled={savingPlatformSettings} type="submit">
            {savingPlatformSettings ? 'Saving...' : 'Save settings'}
          </button>
        </form>
      </article>

      <article className="sd-panel">
      <div className="sd-panel-header">
        <div>
          <h2>Plan Management</h2>
          <p>Feature gates, plan limits, public pricing, and tenant-level platform access.</p>
        </div>
        <button
          className="sd-primary-button"
          onClick={() => setPlanForm({
            name: '',
            slug: '',
            features: ALL_FEATURES.map((feature) => feature.id),
            max_devices: '',
            max_data_streams: '',
            max_users: '',
            max_dashboards: '',
            price_monthly: '',
            currency: 'USD',
            is_public: true,
          })}
          type="button"
        >
          New Plan
        </button>
      </div>
      <div className="sd-super-plan-grid">
        {plans.map((plan) => (
          <article className="sd-super-plan-card" key={plan.id}>
            <header>
              <div>
                <strong>{plan.name}</strong>
                <span className="sd-mono">{plan.slug}</span>
              </div>
              <div className="sd-super-plan-card-flags">
                {plan.is_default && <span className="sd-state-chip is-resolved">Default</span>}
                <span className={`sd-state-chip ${plan.is_public ? 'is-online' : 'is-warning'}`}>
                  {plan.is_public ? 'Public' : 'Hidden'}
                </span>
              </div>
            </header>
            <div className="sd-super-feature-list">
              {ALL_FEATURES.map((feature) => (
                <FeatureBadge active={plan.features?.includes(feature.id)} key={feature.id} label={feature.label} />
              ))}
            </div>
            <div className="sd-super-limit-grid">
              <Detail label="Max Devices" value={formatLimit(plan.max_devices)} />
              <Detail label="Max Data Streams" value={formatLimit(plan.max_data_streams)} />
              <Detail label="Max Users" value={formatLimit(plan.max_users)} />
              <Detail label="Max Dashboards" value={formatLimit(plan.max_dashboards)} />
              <Detail label="Monthly Price" value={formatPlanPrice(plan.price_monthly, plan.currency)} />
            </div>
            <div className="sd-row-actions">
              <button
                onClick={() => setPlanForm({
                  id: plan.id,
                  name: plan.name,
                  slug: plan.slug,
                  features: plan.features || [],
                  max_devices: plan.max_devices == null ? '' : String(plan.max_devices),
                  max_data_streams: plan.max_data_streams == null ? '' : String(plan.max_data_streams),
                  max_users: plan.max_users == null ? '' : String(plan.max_users),
                  max_dashboards: plan.max_dashboards == null ? '' : String(plan.max_dashboards),
                  price_monthly: plan.price_monthly == null ? '' : String(plan.price_monthly),
                  currency: plan.currency || 'USD',
                  is_public: plan.is_public,
                })}
                type="button"
              >
                Edit
              </button>
              {!plan.is_default && (
                <button className="is-danger" onClick={() => handleDeletePlan(plan)} type="button">Delete</button>
              )}
            </div>
          </article>
        ))}
        {plans.length === 0 && <div className="sd-mobile-empty-card">No plans configured yet.</div>}
      </div>
    </article>
    </section>
  );
}

function MetricsPanel({
  metrics,
  plans,
  tenants,
  usage,
}: {
  metrics: PlatformMetrics | null;
  plans: Plan[];
  tenants: Tenant[];
  usage: PlatformUsage | null;
}) {
  const chartColors = ['#0A84FF', '#30D158', '#FF9F0A', '#8B5CF6', '#06B6D4', '#FF453A'];
  const planCounts = plans.map((plan) => ({
    ...plan,
    tenantCount: tenants.filter((tenant) => tenant.plan === plan.slug).length,
  }));
  const planMixData = planCounts.filter((plan) => plan.tenantCount > 0);
  const usageSeries = (usage?.series || []).slice(-30).map((point) => ({
    ...point,
    day: formatChartDate(point.date),
  }));
  const latestUsage = usageSeries[usageSeries.length - 1];
  const readingsWindowTotal = usageSeries.reduce((sum, point) => sum + Number(point.readings || 0), 0);
  const topTenantUsage = [...(usage?.per_tenant || [])]
    .sort((a, b) => Number(b.readings || 0) - Number(a.readings || 0))
    .slice(0, 8)
    .map((tenant) => ({
      ...tenant,
      displayName: shortenLabel(tenant.tenant_name, 24),
    }));
  const totalDevices = metrics?.total_devices ?? tenants.reduce((sum, tenant) => sum + tenant.device_count, 0);
  const totalUsers = metrics?.total_users ?? tenants.reduce((sum, tenant) => sum + tenant.user_count, 0);
  const totalTenants = metrics?.total_tenants ?? tenants.length;
  const deviceLimitTotal = tenants.reduce((sum, tenant) => {
    const plan = plans.find((item) => item.slug === tenant.plan);
    return plan?.max_devices == null ? sum : sum + plan.max_devices;
  }, 0);
  const userLimitTotal = tenants.reduce((sum, tenant) => {
    const plan = plans.find((item) => item.slug === tenant.plan);
    return plan?.max_users == null ? sum : sum + plan.max_users;
  }, 0);
  const paidTenants = tenants.filter((tenant) => tenant.plan && tenant.plan !== 'free').length;
  const capacityRows = [
    {
      label: 'Device seats',
      value: totalDevices,
      limit: deviceLimitTotal || null,
      tone: 'blue',
    },
    {
      label: 'User seats',
      value: totalUsers,
      limit: userLimitTotal || null,
      tone: 'success',
    },
    {
      label: 'Paid tenant mix',
      value: paidTenants,
      limit: totalTenants || null,
      tone: 'warning',
    },
  ];
  const tooltipStyle = {
    background: '#111827',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    color: '#F9FAFB',
  };

  return (
    <section className="sd-super-analytics-grid">
      <article className="sd-panel sd-super-chart-panel sd-super-chart-wide">
        <div className="sd-panel-header">
          <div>
            <h2>Telemetry Volume Trend</h2>
            <p>30-day platform readings with tenant, user, and device growth context.</p>
          </div>
          <div className="sd-super-chart-stat-strip">
            <Detail label="Window Readings" value={formatCompactNumber(readingsWindowTotal)} />
            <Detail label="Latest Devices" value={latestUsage ? formatNumber(latestUsage.devices) : formatNumber(totalDevices)} />
          </div>
        </div>
        <div className="sd-super-chart-body">
          {usageSeries.length === 0 ? (
            <div className="sd-mobile-empty-card">No usage trend available.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={usageSeries} margin={{ top: 12, right: 18, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="platformReadingsGradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.42} />
                    <stop offset="100%" stopColor="#0A84FF" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.07)" vertical={false} />
                <XAxis dataKey="day" stroke="#6B7280" tick={{ fill: '#9CA3AF', fontSize: 11 }} tickLine={false} />
                <YAxis stroke="#6B7280" tick={{ fill: '#9CA3AF', fontSize: 11 }} tickFormatter={formatCompactNumber} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: any, name: any) => [formatCompactNumber(Number(value)), formatMetricName(String(name))]} />
                <Area dataKey="readings" name="readings" stroke="#0A84FF" strokeWidth={3} fill="url(#platformReadingsGradient)" type="monotone" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </article>

      <article className="sd-panel sd-super-chart-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Plan Mix</h2>
            <p>Tenant allocation by commercial plan.</p>
          </div>
        </div>
        <div className="sd-super-donut-layout">
          <div className="sd-super-donut-chart">
            {planMixData.length === 0 ? (
              <div className="sd-mobile-empty-card">No tenant plan allocation yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie
                    data={planMixData}
                    dataKey="tenantCount"
                    innerRadius={58}
                    nameKey="name"
                    outerRadius={86}
                    paddingAngle={3}
                  >
                    {planMixData.map((plan, index) => (
                      <Cell fill={chartColors[index % chartColors.length]} key={plan.id} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value: any) => [formatNumber(Number(value)), 'Tenants']} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="sd-super-donut-legend">
            {planCounts.map((plan, index) => (
              <div key={plan.id}>
                <i style={{ background: chartColors[index % chartColors.length] }} />
                <span>{plan.name}</span>
                <strong>{plan.tenantCount}</strong>
              </div>
            ))}
          </div>
        </div>
      </article>

      <article className="sd-panel sd-super-chart-panel sd-super-chart-wide">
        <div className="sd-panel-header">
          <div>
            <h2>Top Tenants by Readings</h2>
            <p>Highest telemetry volume in the current usage window.</p>
          </div>
        </div>
        <div className="sd-super-chart-body">
          {topTenantUsage.length === 0 ? (
            <div className="sd-mobile-empty-card">No tenant usage available.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topTenantUsage} layout="vertical" margin={{ top: 12, right: 22, left: 22, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.07)" horizontal={false} />
                <XAxis stroke="#6B7280" tick={{ fill: '#9CA3AF', fontSize: 11 }} tickFormatter={formatCompactNumber} tickLine={false} type="number" />
                <YAxis dataKey="displayName" stroke="#9CA3AF" tick={{ fill: '#D1D5DB', fontSize: 12 }} tickLine={false} type="category" width={150} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: any) => [formatCompactNumber(Number(value)), 'Readings']} />
                <Bar dataKey="readings" fill="#0A84FF" radius={[0, 7, 7, 0]}>
                  {topTenantUsage.map((tenant, index) => (
                    <Cell fill={chartColors[index % chartColors.length]} key={tenant.tenant_id} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </article>

      <article className="sd-panel sd-super-chart-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Capacity Utilization</h2>
            <p>Plan-limit pressure for devices, users, and paid workspace mix.</p>
          </div>
        </div>
        <div className="sd-super-capacity-list">
          {capacityRows.map((row) => {
            const percent = row.limit ? Math.min((row.value / row.limit) * 100, 100) : 0;
            return (
              <div className="sd-super-capacity-row" key={row.label}>
                <div>
                  <span>{row.label}</span>
                  <strong>{formatNumber(row.value)}{row.limit ? ` / ${formatNumber(row.limit)}` : ' / Unlimited'}</strong>
                </div>
                <div className="sd-super-capacity-track">
                  <i className={`is-${row.tone}`} style={{ width: `${row.limit ? Math.max(percent, 3) : 100}%` }} />
                </div>
                <small>{row.limit ? `${percent.toFixed(0)}% utilized` : 'No hard limit configured'}</small>
              </div>
            );
          })}
        </div>
      </article>
    </section>
  );
}

function PlatformAuditPanel({
  auditFilters,
  auditLoading,
  auditLogs,
  auditPage,
  auditTotal,
  loadAuditLogs,
  setAuditFilters,
  tenants,
}: {
  auditFilters: AuditFiltersState;
  auditLoading: boolean;
  auditLogs: PlatformAuditLog[];
  auditPage: number;
  auditTotal: number;
  loadAuditLogs: (page: number, filters?: AuditFiltersState) => void;
  setAuditFilters: React.Dispatch<React.SetStateAction<AuditFiltersState>>;
  tenants: Tenant[];
}) {
  const showingStart = auditTotal === 0 ? 0 : auditPage * AUDIT_PAGE_SIZE + 1;
  const showingEnd = Math.min((auditPage + 1) * AUDIT_PAGE_SIZE, auditTotal);
  const canGoPrevious = auditPage > 0;
  const canGoNext = showingEnd < auditTotal;

  function updateFilter(key: keyof AuditFiltersState, value: string) {
    setAuditFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    loadAuditLogs(0);
  }

  function handleReset() {
    setAuditFilters(EMPTY_AUDIT_FILTERS);
    loadAuditLogs(0, EMPTY_AUDIT_FILTERS);
  }

  return (
    <section className="sd-page-stack">
      <article className="sd-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Platform Audit Filters</h2>
            <p>Search across tenant actions, user changes, device operations, and system events.</p>
          </div>
        </div>
        <form className="sd-super-audit-filter-grid" onSubmit={handleSubmit}>
          <label className="sd-field">
            <span>Tenant</span>
            <select value={auditFilters.tenant_id} onChange={(event) => updateFilter('tenant_id', event.target.value)}>
              <option value="">All tenants</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
            <small>Scope platform logs to one tenant, or keep All tenants for global review.</small>
          </label>
          <label className="sd-field">
            <span>Event Type</span>
            <select value={auditFilters.event_type} onChange={(event) => updateFilter('event_type', event.target.value)}>
              <option value="">All events</option>
              <option value="user_action">User action</option>
              <option value="device_action">Device action</option>
              <option value="alert_action">Alert action</option>
              <option value="tenant_action">Tenant action</option>
              <option value="mqtt_action">MQTT action</option>
              <option value="system_action">System action</option>
            </select>
            <small>Filter by the kind of platform activity being investigated.</small>
          </label>
          <label className="sd-field">
            <span>Severity</span>
            <select value={auditFilters.severity} onChange={(event) => updateFilter('severity', event.target.value)}>
              <option value="">All severities</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="critical">Critical</option>
            </select>
            <small>Focus on normal info, warnings, errors, or critical platform events.</small>
          </label>
          <label className="sd-field">
            <span>Action</span>
            <input
              onChange={(event) => updateFilter('action', event.target.value)}
              placeholder="e.g. device.update"
              type="search"
              value={auditFilters.action}
            />
            <small>Action identifier or partial action text.</small>
          </label>
          <label className="sd-field sd-form-span-2">
            <span>Search</span>
            <input
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder="e.g. message, user, tenant, device..."
              type="search"
              value={auditFilters.search}
            />
            <small>Free-text search across audit message, actor, tenant, device, and metadata.</small>
          </label>
          <label className="sd-field">
            <span>Start</span>
            <input
              onChange={(event) => updateFilter('start', event.target.value)}
              type="date"
              value={auditFilters.start}
            />
            <small>Only show events from this date onward.</small>
          </label>
          <label className="sd-field">
            <span>End</span>
            <input
              onChange={(event) => updateFilter('end', event.target.value)}
              type="date"
              value={auditFilters.end}
            />
            <small>Only show events up to this date.</small>
          </label>
          <div className="sd-super-audit-actions">
            <button className="sd-primary-button" disabled={auditLoading} type="submit">
              {auditLoading ? 'Loading...' : 'Apply Filters'}
            </button>
            <button className="sd-secondary-button" onClick={handleReset} type="button">Reset</button>
          </div>
        </form>
      </article>

      <article className="sd-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Platform Audit Logs</h2>
            <p>Showing {showingStart}-{showingEnd} of {auditTotal} events.</p>
          </div>
          <div className="sd-pagination-controls">
            <button disabled={!canGoPrevious || auditLoading} onClick={() => loadAuditLogs(auditPage - 1)} type="button">Previous</button>
            <button disabled={!canGoNext || auditLoading} onClick={() => loadAuditLogs(auditPage + 1)} type="button">Next</button>
          </div>
        </div>
        <div className="sd-table-wrap sd-super-audit-table-wrap">
          <table className="sd-table sd-super-audit-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Tenant</th>
                <th>Severity</th>
                <th>Type</th>
                <th>Action</th>
                <th>User</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr><td className="sd-empty-row" colSpan={7}>{auditLoading ? 'Loading audit logs...' : 'No platform audit logs match this filter.'}</td></tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDateTime(log.created_at)}</td>
                    <td>{log.tenant_name || log.tenant_id}</td>
                    <td><SeverityBadge severity={log.severity} /></td>
                    <td>{formatAuditValue(log.event_type)}</td>
                    <td className="sd-mono">{log.action}</td>
                    <td>{log.user_email || '-'}</td>
                    <td>
                      <span>{log.message}</span>
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <details className="sd-super-metadata">
                          <summary>Metadata</summary>
                          <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                        </details>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="sd-super-audit-card-list">
          {auditLogs.length === 0 ? (
            <div className="sd-mobile-empty-card">{auditLoading ? 'Loading audit logs...' : 'No platform audit logs match this filter.'}</div>
          ) : (
            auditLogs.map((log) => (
              <article className="sd-mobile-record-card" key={log.id}>
                <header className="sd-mobile-record-header">
                  <SeverityBadge severity={log.severity} />
                  <time>{formatDateTime(log.created_at)}</time>
                </header>
                <div className="sd-mobile-record-title">
                  <strong>{log.message}</strong>
                  <span>{log.tenant_name || log.tenant_id}</span>
                </div>
                <div className="sd-mobile-record-grid">
                  <div className="sd-mobile-record-fact">
                    <span>Type</span>
                    <strong>{formatAuditValue(log.event_type)}</strong>
                  </div>
                  <div className="sd-mobile-record-fact">
                    <span>Action</span>
                    <strong className="sd-mono">{log.action}</strong>
                  </div>
                  <div className="sd-mobile-record-fact">
                    <span>User</span>
                    <strong>{log.user_email || '-'}</strong>
                  </div>
                </div>
                {log.metadata && Object.keys(log.metadata).length > 0 && (
                  <details className="sd-super-metadata">
                    <summary>Metadata</summary>
                    <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                  </details>
                )}
              </article>
            ))
          )}
        </div>
      </article>
    </section>
  );
}

function SuperAdminsPanel({
  activateSuperAdmin,
  changingPassword,
  creatingAdmin,
  deactivateSuperAdmin,
  handleChangePassword,
  handleCreateSuperAdmin,
  newAdminEmail,
  newAdminPassword,
  passwordForm,
  setNewAdminEmail,
  setNewAdminPassword,
  setPasswordForm,
  superAdmins,
}: {
  activateSuperAdmin: (admin: SuperAdminAccount) => void;
  changingPassword: boolean;
  creatingAdmin: boolean;
  deactivateSuperAdmin: (admin: SuperAdminAccount) => void;
  handleChangePassword: (event: React.FormEvent) => void;
  handleCreateSuperAdmin: (event: React.FormEvent) => void;
  newAdminEmail: string;
  newAdminPassword: string;
  passwordForm: { current: string; new: string; confirm: string };
  setNewAdminEmail: (value: string) => void;
  setNewAdminPassword: (value: string) => void;
  setPasswordForm: React.Dispatch<React.SetStateAction<{ current: string; new: string; confirm: string }>>;
  superAdmins: SuperAdminAccount[];
}) {
  return (
    <section className="sd-super-admin-grid">
      <article className="sd-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Change Password</h2>
            <p>Update credentials for the current super admin session.</p>
          </div>
        </div>
        <form className="sd-super-form-stack" onSubmit={handleChangePassword}>
          <label className="sd-field">
            <span>Current Password</span>
            <input
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, current: event.target.value }))}
              placeholder="Enter your current password"
              required
              type="password"
              value={passwordForm.current}
            />
            <small>Required to authorize this credential change.</small>
          </label>
          <label className="sd-field">
            <span>New Password</span>
            <input
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, new: event.target.value }))}
              placeholder="Enter a new secure password"
              required
              type="password"
              value={passwordForm.new}
            />
            <small>Use a strong password for platform-level access.</small>
          </label>
          <label className="sd-field">
            <span>Confirm Password</span>
            <input
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirm: event.target.value }))}
              placeholder="Re-enter the new password"
              required
              type="password"
              value={passwordForm.confirm}
            />
            <small>Must exactly match the new password.</small>
          </label>
          <button className="sd-primary-button" disabled={changingPassword} type="submit">
            {changingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </article>
      <article className="sd-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Create Super Admin</h2>
            <p>Create an additional platform-level administrator.</p>
          </div>
        </div>
        <form className="sd-super-form-stack" onSubmit={handleCreateSuperAdmin}>
          <label className="sd-field">
            <span>Email</span>
            <input
              onChange={(event) => setNewAdminEmail(event.target.value)}
              placeholder="e.g. admin@seedronics.com"
              required
              type="email"
              value={newAdminEmail}
            />
            <small>Email for the new platform-level administrator.</small>
          </label>
          <label className="sd-field">
            <span>Password</span>
            <input
              onChange={(event) => setNewAdminPassword(event.target.value)}
              placeholder="Enter a temporary secure password"
              required
              type="password"
              value={newAdminPassword}
            />
            <small>Temporary password this super admin should rotate after first login.</small>
          </label>
          <button className="sd-primary-button" disabled={creatingAdmin} type="submit">
            {creatingAdmin ? 'Creating...' : 'Create Super Admin'}
          </button>
        </form>
      </article>
      <article className="sd-panel sd-super-admin-directory">
        <div className="sd-panel-header">
          <div>
            <h2>Super Admin Directory</h2>
            <p>Activate or deactivate platform-level administrators.</p>
          </div>
        </div>
        <div className="sd-super-admin-list">
          {superAdmins.length === 0 ? (
            <div className="sd-mobile-empty-card">No super admins found.</div>
          ) : (
            superAdmins.map((admin) => (
              <article className="sd-super-admin-row" key={admin.id}>
                <div>
                  <strong>{admin.email}</strong>
                  <span>Created {formatDate(admin.created_at)}</span>
                  {admin.deactivated_at && <span>Deactivated {formatDate(admin.deactivated_at)}</span>}
                </div>
                <div className="sd-row-actions">
                  <span className={`sd-status-label ${admin.is_active ? 'is-online' : 'is-offline'}`}>
                    <i />
                    {admin.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {admin.is_active ? (
                    <button className="is-danger" onClick={() => deactivateSuperAdmin(admin)} type="button">Deactivate</button>
                  ) : (
                    <button onClick={() => activateSuperAdmin(admin)} type="button">Activate</button>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
      </article>
    </section>
  );
}

function StatCard({ detail, label, tone, value }: { detail: string; label: string; tone: 'blue' | 'danger' | 'success' | 'warning'; value: string }) {
  return (
    <article className={`sd-kpi-card sd-kpi-${tone}`}>
      <div className="sd-kpi-label">{label}</div>
      <div className="sd-kpi-value">{value}</div>
      <div className="sd-kpi-detail">{detail}</div>
      <div className="sd-kpi-footer"><span>Platform scope</span></div>
    </article>
  );
}

function CommandCenterMetric({
  detail,
  label,
  tone,
  value,
}: {
  detail: string;
  label: string;
  tone: 'blue' | 'danger' | 'neutral' | 'success' | 'warning';
  value: string;
}) {
  return (
    <article className={`sd-super-command-metric is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function Detail({ label, mono, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div className="sd-super-detail">
      <span>{label}</span>
      <strong className={mono ? 'sd-mono' : undefined}>{value}</strong>
    </div>
  );
}

function TenantTrialStatus({ tenant }: { tenant: Tenant }) {
  const ended = !!tenant.trial_ends_at && tenant.is_trial !== true && (tenant.days_remaining ?? 0) <= 0;
  const statusClass = tenant.is_trial ? 'is-maintenance' : ended ? 'is-offline' : '';
  const label = tenant.is_trial
    ? `${tenant.days_remaining ?? 0}d trial`
    : ended
      ? 'Trial ended'
      : 'No trial';

  return (
    <span className={`sd-status-label sd-trial-status ${statusClass}`}>
      <i />
      {label}
    </span>
  );
}

function TenantStatus({ tenant }: { tenant: Tenant }) {
  return (
    <span className={`sd-status-label ${tenant.is_active ? 'is-online' : 'is-offline'}`}>
      <i />
      {tenant.is_active ? 'Active' : 'Suspended'}
    </span>
  );
}

function UserLifecycleStatus({ account }: { account: SuperAdminUser }) {
  if (account.suspended_by_platform) {
    return (
      <span className="sd-status-label is-offline">
        <i />
        Suspended
      </span>
    );
  }

  if (!account.is_active) {
    return (
      <span className="sd-status-label is-maintenance">
        <i />
        Inactive
      </span>
    );
  }

  return (
    <span className="sd-status-label is-online">
      <i />
      Active
    </span>
  );
}

function VerificationStatusBadge({ account }: { account: SuperAdminUser }) {
  return (
    <span className={`sd-state-chip ${account.email_verified_at ? 'is-resolved' : 'is-open'}`}>
      {account.email_verified_at ? 'Verified' : 'Unverified'}
    </span>
  );
}

function FeatureBadge({ active, label }: { active?: boolean; label: string }) {
  return (
    <span className={`sd-super-feature-badge ${active ? 'is-active' : ''}`}>
      {label}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: PlatformAuditLog['severity'] }) {
  return (
    <span className={`sd-super-severity is-${severity}`}>
      {severity}
    </span>
  );
}

function getInitials(email: string) {
  return email.slice(0, 1).toUpperCase();
}

function mergeTenantPlanUpdate(tenant: Tenant, update: Partial<Tenant>, requestedPlan: string): Tenant {
  const basePlan = update.base_plan || update.plan || requestedPlan;
  const effectivePlan = tenant.is_trial
    ? tenant.effective_plan || update.effective_plan || basePlan
    : update.effective_plan || basePlan;

  return {
    ...tenant,
    ...update,
    plan: update.plan || basePlan,
    base_plan: basePlan,
    effective_plan: effectivePlan,
  };
}

function mergeTenantTrialUpdate(tenant: Tenant, update: Partial<Tenant>, trialPlanSlug: string): Tenant {
  const isTrial = update.is_trial === true;
  const basePlan = update.base_plan || tenant.base_plan || update.plan || tenant.plan || 'free';
  const effectivePlan = update.effective_plan || (isTrial ? trialPlanSlug || 'enterprise' : basePlan);

  return {
    ...tenant,
    ...update,
    plan: update.plan || tenant.plan || basePlan,
    base_plan: basePlan,
    effective_plan: effectivePlan,
    trial_ends_at: update.trial_ends_at ?? null,
    is_trial: isTrial,
    days_remaining: typeof update.days_remaining === 'number' ? update.days_remaining : 0,
  };
}

function getTenantBasePlanSlug(tenant: Tenant) {
  return tenant.base_plan || tenant.plan || 'free';
}

function getTenantEffectivePlanSlug(tenant: Tenant) {
  return tenant.effective_plan || (tenant.is_trial ? 'enterprise' : getTenantBasePlanSlug(tenant));
}

function formatPlanLabel(value?: string | null) {
  if (!value) return 'Unknown';
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getDefaultTrialDays(value?: string | number | null) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : 30;
}

function getTrialEndIso(days: number) {
  const end = new Date();
  end.setDate(end.getDate() + Math.max(1, days));
  end.setSeconds(0, 0);
  return end.toISOString();
}

function formatTrialDateInput(value: string | null | undefined, fallbackDays: number) {
  const source = value ? new Date(value) : new Date(getTrialEndIso(Math.max(1, fallbackDays || 1)));
  if (Number.isNaN(source.getTime())) return '';
  const local = new Date(source.getTime() - source.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function trialDateInputToIso(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
}

function formatTenantTrialSummary(tenant: Tenant) {
  if (tenant.is_trial) {
    const days = tenant.days_remaining ?? 0;
    return `${formatPlanLabel(getTenantEffectivePlanSlug(tenant))} trial, ${days} day${days === 1 ? '' : 's'} remaining`;
  }
  if (tenant.trial_ends_at) return `Ended ${formatDate(tenant.trial_ends_at)}`;
  return 'No active trial';
}

function formatDate(value?: string) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value?: string) {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

function formatNumber(value: number) {
  return value.toLocaleString();
}

function formatCompactNumber(value: number) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value >= 1000000 ? 1 : 0,
    notation: 'compact',
  }).format(value);
}

function formatLimit(value: number | null | undefined) {
  return value == null ? 'Unlimited' : formatNumber(value);
}

function getPlanPriceNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatPlanPrice(value: number | string | null | undefined, currency?: string | null) {
  const numeric = getPlanPriceNumber(value);
  if (numeric === null) return 'Custom';
  if (numeric === 0) return 'Free';
  try {
    return `${new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: numeric % 1 === 0 ? 0 : 2,
    }).format(numeric)}/mo`;
  } catch {
    return `${currency || '$'} ${numeric.toLocaleString()}/mo`;
  }
}

function formatChartDate(value: string) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatMetricName(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatAuditValue(value: string) {
  return value.replace(/_/g, ' ');
}

function shortenLabel(value: string, maxLength: number) {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(maxLength - 1, 1))}...`;
}

type SuperAdminIconName = 'building' | 'users' | 'layers' | 'chart' | 'health' | 'support' | 'audit' | 'shield';

function SuperAdminIcon({ icon }: { icon: SuperAdminIconName }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
    viewBox: '0 0 24 24',
  };

  if (icon === 'building') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M4 21h16" />
        <path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />
        <path d="M9 8h1" />
        <path d="M14 8h1" />
        <path d="M9 12h1" />
        <path d="M14 12h1" />
        <path d="M10 21v-5h4v5" />
      </svg>
    );
  }

  if (icon === 'layers') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="m12 3 9 5-9 5-9-5 9-5Z" />
        <path d="m3 12 9 5 9-5" />
        <path d="m3 16 9 5 9-5" />
      </svg>
    );
  }

  if (icon === 'users') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M16 19v-1a4 4 0 0 0-8 0v1" />
        <circle cx="12" cy="9" r="3" />
        <path d="M20 19v-1a3 3 0 0 0-3-3" />
        <path d="M17 6a3 3 0 0 1 0 6" />
      </svg>
    );
  }

  if (icon === 'chart') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="m7 15 3-4 3 2 4-7" />
        <path d="M17 6h3v3" />
      </svg>
    );
  }

  if (icon === 'audit') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M8 4h8" />
        <path d="M9 2h6v4H9z" />
        <path d="M6 4H5a2 2 0 0 0-2 2v14h18V6a2 2 0 0 0-2-2h-1" />
        <path d="M7 11h10" />
        <path d="M7 15h6" />
      </svg>
    );
  }

  if (icon === 'health') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M20 12c0 5-7.5 8.5-8 8.5S4 17 4 12a8 8 0 0 1 16 0Z" />
        <path d="M8 12h2l1.3-3 2.2 6 1.4-3H17" />
      </svg>
    );
  }

  if (icon === 'support') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M4 10a8 8 0 0 1 16 0v5a3 3 0 0 1-3 3h-2" />
        <path d="M6 15h2v-5H6a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2Z" />
        <path d="M18 15h-2v-5h2a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2Z" />
        <path d="M13 18h-2a1 1 0 0 1 0-2h2a1 1 0 0 1 0 2Z" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" {...common}>
      <path d="M12 3 5 6v5c0 4.2 2.8 8.1 7 10 4.2-1.9 7-5.8 7-10V6l-7-3Z" />
      <path d="m9.5 12 1.7 1.7 3.6-4" />
    </svg>
  );
}
